import { sanitizeCssPaintValue } from './blockDesign';
import { isCarouselSlideMedia, isImageBackgroundValue } from './slideBackground';
import {
  appendFieldHideMeta,
  isEditorFieldHidden,
  parseFieldHideFromBlock,
  parseFieldHideMetaLine,
} from './editorFieldVisibility';
import {
  formatSiteIconToken,
  parseSiteIconPrefix,
  SITE_ICON_GLYPH_PREFIX_RE,
} from './siteIcons';
import { scrubCloudCheckoutMarketingCopy } from './commerceCopyHygiene';

const BLOCK_SEP = /\n---\n/;

type Hideable<K extends string> = Partial<Record<`${K}Hide`, string>>;

export type QaItem = { question: string; answer: string } & Hideable<'question' | 'answer'>;
export type CardItem = { title: string; description: string; imageUrl?: string } & Hideable<'title' | 'description'>;
export type PricingPlan = { name: string; price: string; description: string } & Hideable<'name' | 'price' | 'description'>;
export type TabItem = { label: string; content: string } & Hideable<'label' | 'content'>;
export type StepItem = { title: string; description: string } & Hideable<'title' | 'description'>;
export type BlogGridItem = { title: string; date: string; excerpt: string; link: string };
export type TeamMember = { name: string; role: string; bio: string; imageUrl?: string };
export type ProductGridItem = { name: string; price: string; description: string; link: string; imageUrl?: string };
export type TimelineEntry = { year: string; title: string; description: string } & Hideable<'year' | 'title' | 'description'>;
export type MenuCategory = { categoryName: string; menuItems: string };
export type StatItem = { value: string; label: string } & Hideable<'value' | 'label'>;
export type IconListItem = { icon: string; text: string } & Hideable<'text'>;
export type ProgressBarItem = { label: string; percent: string } & Hideable<'label' | 'percent'>;
export type NavLink = { label: string; url: string; children?: NavLink[] } & Hideable<'label'>;
export type FooterColumn = { columnHeading: string; columnLines: string };
export type TestimonialQuote = { quote: string; imageUrl?: string } & Hideable<'quote'>;
export type PricingTogglePlan = { name: string; monthlyPrice: string; annualPrice: string; features: string };

/** Indent depth for nested navbar rows (2 spaces or 1 tab = one level). */
function navLineDepth(line: string): number {
  const match = line.match(/^(\t| {2})+/);
  if (!match) return 0;
  const raw = match[0];
  if (raw.includes('\t')) return raw.replace(/ /g, '').length;
  return Math.floor(raw.length / 2);
}

/** Split `Label | /path` (also used for CTA / hero buttons stored in one field). */
export function parseNavLinkRow(line: string): { label: string; url: string } {
  const trimmed = line.replace(/^[\t ]+/, '').trim();
  if (!trimmed.includes('|')) {
    return { label: trimmed || '', url: '' };
  }
  const [label, url] = trimmed.split('|').map((part) => part?.trim() ?? '');
  return { label: label || '', url: url || '' };
}

/**
 * Resolve button label + href when editors paste `Label | /path` into the label
 * field (or leave ctaLink empty). Explicit ctaLink always wins.
 */
export function resolveCtaLabelHref(
  labelRaw: string,
  linkRaw: string,
): { label: string; href: string } {
  const link = (linkRaw || '').trim();
  const label = (labelRaw || '').trim();
  if (!label) return { label: '', href: link };
  if (link) return { label, href: link };
  const parsed = parseNavLinkRow(label);
  if (parsed.url) return { label: parsed.label, href: parsed.url };
  return { label, href: '' };
}

/**
 * Build a nav tree from "Label | url" rows. Indented lines (2 spaces / tab)
 * become children of the nearest less-indented ancestor.
 */
export function buildNavLinkTree(lines: string[]): NavLink[] {
  const roots: NavLink[] = [];
  const stack: Array<{ depth: number; link: NavLink }> = [];

  for (const rawLine of lines) {
    if (!rawLine.replace(/\u200B/g, '').trim()) continue;
    if (parseFieldHideMetaLine(rawLine.trim())) continue;
    const depth = navLineDepth(rawLine);
    const { label, url } = parseNavLinkRow(rawLine);
    if (!label && !url) continue;
    const link: NavLink = { label: label || 'Link', url: url || '' };

    while (stack.length && stack[stack.length - 1].depth >= depth) {
      stack.pop();
    }
    if (stack.length === 0 || depth === 0) {
      roots.push(link);
      stack.length = 0;
      stack.push({ depth: 0, link });
    } else {
      const parent = stack[stack.length - 1].link;
      if (!parent.children) parent.children = [];
      parent.children.push(link);
      stack.push({ depth, link });
    }
  }
  return roots;
}

/** Flatten a nav tree back to indented "Label | url" lines (no hide meta). */
export function flattenNavLinkTree(links: NavLink[], depth = 0): string[] {
  const pad = '  '.repeat(depth);
  const rows: string[] = [];
  for (const link of links) {
    const label = (link.label || '').trim();
    const url = (link.url || '').trim();
    rows.push(`${pad}${label} | ${url}`.trimEnd());
    if (link.children?.length) {
      rows.push(...flattenNavLinkTree(link.children, depth + 1));
    }
  }
  return rows;
}

/**
 * Parse one navbar / footer link block. Supports nested children via indented
 * lines under the parent "Label | url" row. Hide meta applies to the parent.
 * Editor may also pass `children` as a multiline string of nested rows.
 */
function parseNavLinkBlock(block: string): NavLink {
  const { body, hides } = parseFieldHideFromBlock(block);
  const lines = body.split('\n');
  const first = lines[0] ?? '';
  const { label, url } = parseNavLinkRow(first);
  const nestedFromIndent = lines.length > 1
    ? buildNavLinkTree(lines.slice(1).map((line) => {
      // Relative indent: treat body lines as already nested under parent.
      const depth = navLineDepth(line);
      const pad = depth > 0 ? line : `  ${line.trim()}`;
      return pad;
    }))
    : [];
  const result: NavLink = {
    label: label || '',
    url: url || '',
    ...hides,
  };
  if (nestedFromIndent.length) {
    result.children = nestedFromIndent;
  }
  return result;
}

function childrenToMultiline(children: NavLink[] | undefined): string {
  if (!children?.length) return '';
  return flattenNavLinkTree(children, 0).join('\n');
}

function parseChildrenField(raw: unknown): NavLink[] | undefined {
  if (Array.isArray(raw)) {
    const links = (raw as NavLink[])
      .map((item) => ({
        label: String(item?.label || '').trim() || 'Link',
        url: String(item?.url || '').trim(),
        ...(item?.children?.length ? { children: item.children } : {}),
      }))
      .filter((item) => item.label || item.url);
    return links.length ? links : undefined;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const tree = buildNavLinkTree(raw.split('\n'));
    return tree.length ? tree : undefined;
  }
  return undefined;
}

function serializeNavLinkBlock(link: NavLink): string {
  const children = parseChildrenField(link.children) ?? link.children;
  const head = `${(link.label || '').trim()} | ${(link.url || '').trim()}`.trim();
  const childRows = children?.length ? flattenNavLinkTree(children, 1) : [];
  const body = [head, ...childRows].join('\n');
  return appendFieldHideMeta(body, link, ['label']);
}

function splitBlocks(raw: string): string[] {
  if (!raw) return [];
  // Keep empty segments between ---. Only strip trailing newlines / BOM —
  // never .trim() the whole block (that drops a leading blank line and
  // promotes the next field, e.g. empty product name → price becomes name).
  return raw.split(BLOCK_SEP).map((s) => s.replace(/^\uFEFF/, '').replace(/\n+$/, ''));
}

function joinBlocks(blocks: string[]): string {
  return blocks.join('\n---\n');
}

/**
 * Title on line 0, body on following lines. Never trim the joined string —
 * that would promote body into the title slot when title is cleared mid-edit
 * (same class of bug as empty navbar brand).
 */
function joinTitleBody(title: string, body: string): string {
  if (!body.trim()) return title;
  return `${title}\n${body}`;
}

function splitContentLines(content: string): string[] {
  return (content ?? '').split('\n');
}

export function parseQaItems(source: string, contentFallback?: string): { heading: string; items: QaItem[] } {
  const blocks = splitBlocks(source);
  const items = blocks.map((block) => {
    const { body, hides } = parseFieldHideFromBlock(block);
    const [question, ...rest] = body.split('\n');
    return { question: (question || '').trim(), answer: rest.join('\n').trim(), ...hides };
  });
  return { heading: contentFallback?.trim() || '', items };
}

export function serializeQaItems(heading: string, items: Array<QaItem & Record<string, string>>): { content: string; items: string } {
  // Do not drop blank items — StructuredBlockEditor round-trips through
  // serialize on every keystroke; filtering empties makes rows vanish mid-edit.
  const serialized = joinBlocks(
    items.map((item) => appendFieldHideMeta(`${item.question}\n${item.answer}`, item, ['question', 'answer'])),
  );
  return { content: heading, items: serialized };
}

function cardItemBlockBody(card: CardItem & { imageUrl?: string }): string {
  const lines: string[] = [];
  if ((card.imageUrl || '').trim()) lines.push(card.imageUrl!.trim());
  lines.push(card.title, card.description);
  return lines.join('\n');
}

export function parseCardItems(itemsRaw: string, headingRaw?: string, contentRaw?: string): { heading: string; cards: CardItem[] } {
  const source = itemsRaw.trim() || contentRaw?.trim() || '';
  const blocks = splitBlocks(source);
  const cards = blocks.map((block) => {
    const { body, hides } = parseFieldHideFromBlock(block);
    const lines = body.split('\n');
    let imageUrl = '';
    let offset = 0;
    const first = (lines[0] || '').trim();
    if (first && isCarouselSlideMedia(first) && isImageBackgroundValue(first)) {
      imageUrl = first;
      offset = 1;
    }
    const title = (lines[offset] || '').trim();
    const description = lines.slice(offset + 1).join('\n').trim();
    return { title, description, imageUrl, ...hides };
  });
  const heading = itemsRaw.trim() ? (headingRaw?.trim() || '') : '';
  return { heading, cards };
}

export function serializeCardItems(heading: string, cards: Array<CardItem & Record<string, string>>): { content: string; items: string } {
  const serialized = joinBlocks(
    cards.map((card) => appendFieldHideMeta(cardItemBlockBody(card), card, ['title', 'description'])),
  );
  return {
    content: heading,
    items: serialized,
  };
}

export function parsePricingPlans(plansRaw: string, headingRaw?: string): { heading: string; plans: PricingPlan[] } {
  const blocks = splitBlocks(plansRaw);
  const plans = blocks.map((block) => {
    const { body, hides } = parseFieldHideFromBlock(block);
    const [name, price, ...rest] = body.split('\n');
    return {
      name: (name || '').trim(),
      price: (price || '').trim(),
      description: rest.join('\n').trim(),
      ...hides,
    };
  });
  return { heading: headingRaw?.trim() || 'Pricing', plans };
}

export function serializePricingPlans(heading: string, plans: Array<PricingPlan & Record<string, string>>): { content: string; plans: string } {
  const serialized = joinBlocks(
    plans.map((plan) => appendFieldHideMeta(`${plan.name}\n${plan.price}\n${plan.description}`, plan, ['name', 'price', 'description'])),
  );
  return { content: heading.trim() || 'Pricing', plans: serialized };
}

export function parseTabItems(contentRaw: string): { heading: string; tabs: TabItem[] } {
  const blocks = splitBlocks(contentRaw);
  const tabs = blocks.map((block) => {
    const { body, hides } = parseFieldHideFromBlock(block);
    const [label, ...rest] = body.split('\n');
    return { label: (label || '').trim(), content: rest.join('\n').trim(), ...hides };
  });
  return { heading: '', tabs };
}

export function serializeTabItems(tabs: Array<TabItem & Record<string, string>>): { content: string } {
  const serialized = joinBlocks(
    tabs.map((tab) => appendFieldHideMeta(`${tab.label}\n${tab.content}`, tab, ['label', 'content'])),
  );
  return { content: serialized };
}

export function parseStepItems(contentRaw: string): { heading: string; steps: StepItem[] } {
  const blocks = splitBlocks(contentRaw);
  const steps = blocks.map((block) => {
    const { body, hides } = parseFieldHideFromBlock(block);
    const [title, ...rest] = body.split('\n');
    return { title: (title || '').trim(), description: rest.join('\n').trim(), ...hides };
  });
  return { heading: '', steps };
}

export function serializeStepItems(steps: Array<StepItem & Record<string, string>>): { content: string } {
  const serialized = joinBlocks(
    steps.map((step) => appendFieldHideMeta(`${step.title}\n${step.description}`, step, ['title', 'description'])),
  );
  return { content: serialized };
}

export function parseBlogGridItems(itemsRaw: string, headingRaw?: string): { heading: string; posts: Array<BlogGridItem & Record<string, string>> } {
  const blocks = splitBlocks(itemsRaw);
  const posts = blocks.map((block) => {
    const { body, hides } = parseFieldHideFromBlock(block);
    const lines = body.split('\n');
    const title = (lines[0] || '').trim();
    const date = (lines[1] || '').trim();
    const link = lines.length >= 4 ? (lines[lines.length - 1] || '').trim() : '';
    const excerpt = lines.length >= 4 ? lines.slice(2, -1).join('\n').trim() : lines.slice(2).join('\n').trim();
    return {
      title,
      date,
      excerpt,
      link: link || `/blog/${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
      ...hides,
    };
  });
  return { heading: headingRaw?.trim() || 'Latest Articles', posts };
}

export function serializeBlogGridItems(heading: string, posts: Array<BlogGridItem & Record<string, string>>): { content: string; items: string } {
  const serialized = joinBlocks(
    posts.map((post) =>
      appendFieldHideMeta(`${post.title}\n${post.date}\n${post.excerpt}\n${post.link}`, post, ['title', 'date', 'excerpt', 'link']),
    ),
  );
  return { content: heading.trim() || 'Latest Articles', items: serialized };
}

/** Markdown / price-list separator between header and body (`---`). */
export function isTableSeparatorLine(line: string): boolean {
  const t = line.trim();
  return t === '---' || /^[-|:\s]+$/.test(t);
}

/** Section band row: `## Anfahrt` alone (optional empty trailing cells). */
export function isTableSectionRow(cells: string[]): boolean {
  const first = (cells[0] || '').trim();
  const restEmpty = cells.slice(1).every((c) => !c);
  return /^##\s+\S/.test(first) && (cells.length === 1 || restEmpty);
}

export function tableSectionLabel(cells: string[]): string {
  return (cells[0] || '').replace(/^##\s+/, '').trim();
}

export function makeTableSectionRow(label: string, colCount = 1): string[] {
  const title = label.trim() ? `## ${label.trim()}` : '## Section';
  const row = [title];
  while (row.length < Math.max(1, colCount)) row.push('');
  return row;
}

/**
 * Split a comparison/price-list row on `|` or tab.
 * `serializeComparisonTable` joins with ` | `, so each pipe may carry one
 * padding space on either side — strip only that padding. Do NOT `.trim()`:
 * mid-edit spaces (e.g. `69,56 ` before typing `€`) must survive the
 * serialize→parse round-trip that runs on every VirtualKeyboard keystroke.
 */
export function splitTableRowCells(row: string): string[] {
  if (row.includes('|')) {
    const parts = row.split('|');
    return parts.map((cell, i) => {
      let c = cell;
      if (i > 0 && c.startsWith(' ')) c = c.slice(1);
      if (i < parts.length - 1 && c.endsWith(' ')) c = c.slice(0, -1);
      return c;
    });
  }
  return row.split('\t');
}

export function parseComparisonTable(contentRaw: string): { headers: string[]; rows: string[][] } {
  // Preserve empty lines so clearing a row doesn't drop sibling rows.
  // Skip `---` / markdown rule lines (price-list convention; renderer also skips).
  // Do not trimEnd rows — that drops a trailing space in the last cell.
  const rawRows = contentRaw
    .split(/\r?\n/)
    .filter((r) => !isTableSeparatorLine(r));
  if (rawRows.length === 0 || (rawRows.length === 1 && !rawRows[0])) {
    return { headers: ['Feature', 'Starter', 'Pro'], rows: [['Price', '$9/mo', '$29/mo']] };
  }
  const rows = rawRows.map((row) => splitTableRowCells(row));
  const [head, ...body] = rows;
  return {
    headers: head?.length ? head : ['Feature'],
    rows: body.length > 0 ? body : [['']],
  };
}

export type SerializeComparisonTableOptions = {
  /** Insert `---` after the header row (price-list / table block convention). */
  includeSeparator?: boolean;
};

export function serializeComparisonTable(
  headers: string[],
  rows: string[][],
  opts?: SerializeComparisonTableOptions,
): { content: string } {
  // Keep blank rows/cells — filtering them mid-edit collapses the table.
  // Section rows serialize as a single `## Label` line (no empty trailing cells).
  const lines: string[] = [headers.join(' | ')];
  if (opts?.includeSeparator) lines.push('---');
  for (const row of rows) {
    if (isTableSectionRow(row)) {
      lines.push((row[0] || '').trim());
    } else {
      lines.push(row.join(' | '));
    }
  }
  return { content: lines.join('\n') };
}

function teamGridMemberBlockBody(member: TeamMember & { imageUrl?: string }): string {
  const lines: string[] = [];
  if ((member.imageUrl || '').trim()) lines.push(member.imageUrl!.trim());
  lines.push(member.name, member.role, member.bio);
  return lines.join('\n');
}

export function parseTeamGridMembers(membersRaw: string, headingRaw?: string): { heading: string; members: Array<TeamMember & Record<string, string>> } {
  const blocks = splitBlocks(membersRaw);
  const members = blocks.map((block) => {
    const { body, hides } = parseFieldHideFromBlock(block);
    const lines = body.split('\n');
    let imageUrl = '';
    let offset = 0;
    const first = (lines[0] || '').trim();
    if (first && isCarouselSlideMedia(first) && isImageBackgroundValue(first)) {
      imageUrl = first;
      offset = 1;
    }
    const name = (lines[offset] || '').trim();
    const role = (lines[offset + 1] || '').trim();
    const bio = lines.slice(offset + 2).join('\n').trim();
    return {
      name,
      role,
      bio,
      imageUrl,
      ...hides,
    };
  });
  return { heading: headingRaw?.trim() || 'Our Team', members };
}

export function serializeTeamGridMembers(heading: string, members: Array<TeamMember & Record<string, string>>): { content: string; members: string } {
  const serialized = joinBlocks(
    members.map((member) => appendFieldHideMeta(teamGridMemberBlockBody(member), member, ['name', 'role', 'bio'])),
  );
  return { content: heading.trim() || 'Our Team', members: serialized };
}

function productGridBlockBody(product: ProductGridItem): string {
  const lines: string[] = [];
  if ((product.imageUrl || '').trim()) lines.push(product.imageUrl!.trim());
  lines.push(product.name, product.price, product.description);
  if (product.link.trim()) lines.push(product.link.trim());
  while (lines.length > 2 && !lines[lines.length - 1]) lines.pop();
  return lines.join('\n');
}

export function parseProductGridItems(itemsRaw: string, headingRaw?: string): { heading: string; products: Array<ProductGridItem & Record<string, string>> } {
  const blocks = splitBlocks(itemsRaw);
  const products = blocks.map((block) => {
    const { body, hides } = parseFieldHideFromBlock(block);
    const lines = body.split('\n').map((line) => line.trim());
    while (lines.length > 3 && !lines[lines.length - 1]) lines.pop();
    let imageUrl = '';
    let offset = 0;
    if (lines[0] && isCarouselSlideMedia(lines[0]) && isImageBackgroundValue(lines[0])) {
      imageUrl = lines[0];
      offset = 1;
    }
    const name = lines[offset] || '';
    const price = lines[offset + 1] || '';
    const rest = lines.slice(offset + 2);
    const maybeLink = rest.length >= 1 && /^\/|^https?:\/\//.test(rest[rest.length - 1] || '') ? rest[rest.length - 1] : '';
    const description = maybeLink ? rest.slice(0, -1).join('\n').trim() : rest.join('\n').trim();
    return { name, price, description, link: maybeLink, imageUrl, ...hides };
  });
  return { heading: headingRaw?.trim() || 'Our Products', products };
}

export function serializeProductGridItems(heading: string, products: Array<ProductGridItem & Record<string, string>>): { content: string; items: string } {
  const serialized = joinBlocks(
    products.map((product) =>
      appendFieldHideMeta(productGridBlockBody(product), product, ['name', 'price', 'description', 'link']),
    ),
  );
  return { content: heading.trim() || 'Our Products', items: serialized };
}

export function parseTimelineEntries(contentRaw: string): { entries: TimelineEntry[] } {
  if (!contentRaw) return { entries: [] };
  const hasHideMeta = contentRaw.split('\n').some((line) => parseFieldHideMetaLine(line.trim()));
  const useBlocks = contentRaw.includes('---') || hasHideMeta;
  if (useBlocks) {
    const blocks = contentRaw.includes('---') ? splitBlocks(contentRaw) : [contentRaw];
    const entries = blocks.map((block) => {
      const { body, hides } = parseFieldHideFromBlock(block);
      const parts = body.split('|').map((part) => part.trim());
      return {
        year: parts[0] || '',
        title: parts[1] || '',
        description: parts[2] || '',
        ...hides,
      };
    });
    return { entries };
  }
  const entries = contentRaw.split('\n').map((line) => {
    const parts = line.split('|').map((part) => part.trim());
    return {
      year: parts[0] || '',
      title: parts[1] || '',
      description: parts[2] || '',
    };
  });
  return { entries };
}

export function serializeTimelineEntries(entries: Array<TimelineEntry & Record<string, string>>): { content: string } {
  const blocks = entries.map((entry) =>
    appendFieldHideMeta(`${entry.year} | ${entry.title} | ${entry.description}`, entry, ['year', 'title', 'description']),
  );
  const hasHide = entries.some(
    (entry) =>
      isEditorFieldHidden(entry.yearHide) ||
      isEditorFieldHidden(entry.titleHide) ||
      isEditorFieldHidden(entry.descriptionHide),
  );
  if (!hasHide) {
    return { content: blocks.join('\n') };
  }
  if (blocks.length === 1) {
    return { content: blocks[0] };
  }
  return { content: joinBlocks(blocks) };
}

export function parseMenuSectionCategories(itemsRaw: string, headingRaw?: string): { heading: string; categories: Array<MenuCategory & Record<string, string>> } {
  const blocks = splitBlocks(itemsRaw);
  const categories = blocks.map((block) => {
    const { body, hides } = parseFieldHideFromBlock(block);
    const lines = body.split('\n');
    const [categoryName, ...itemLines] = lines;
    return { categoryName: (categoryName || '').trim(), menuItems: itemLines.join('\n').trim(), ...hides };
  });
  return { heading: headingRaw?.trim() || 'Our Menu', categories };
}

export function serializeMenuSectionCategories(heading: string, categories: Array<MenuCategory & Record<string, string>>): { content: string; items: string } {
  const serialized = joinBlocks(
    categories.map((category) =>
      appendFieldHideMeta(`${category.categoryName}\n${category.menuItems}`, category, ['categoryName', 'menuItems']),
    ),
  );
  return { content: heading.trim() || 'Our Menu', items: serialized };
}

export function parseStatItems(contentRaw: string): { items: StatItem[] } {
  if (!contentRaw) return { items: [] };
  const hasHideMeta = contentRaw.split('\n').some((line) => parseFieldHideMetaLine(line.trim()));
  const useBlocks = contentRaw.includes('---') || hasHideMeta;
  const blocks = useBlocks
    ? (contentRaw.includes('---') ? splitBlocks(contentRaw) : [contentRaw])
    : contentRaw.split('\n').filter(Boolean);
  const items = blocks.map((block) => {
    const { body, hides } = useBlocks ? parseFieldHideFromBlock(block) : { body: block, hides: {} as Record<string, string> };
    const [value, label] = body.split('|').map((part) => part.trim());
    return { value: value || '', label: label || '', ...hides };
  });
  return { items };
}

export function serializeStatItems(items: Array<StatItem & Record<string, string>>): { content: string } {
  const blocks = items.map((item) =>
    appendFieldHideMeta(`${item.value} | ${item.label}`, item, ['value', 'label']),
  );
  const hasHide = items.some(
    (item) => isEditorFieldHidden(item.valueHide) || isEditorFieldHidden(item.labelHide),
  );
  if (!hasHide) {
    return { content: blocks.join('\n') };
  }
  if (blocks.length === 1) {
    return { content: blocks[0] };
  }
  return { content: joinBlocks(blocks) };
}

const ICON_PREFIX_RE = SITE_ICON_GLYPH_PREFIX_RE;

export function parseIconListItems(itemsRaw: string, headingRaw?: string, contentRaw?: string): { heading: string; items: IconListItem[] } {
  let heading = scrubCloudCheckoutMarketingCopy(headingRaw?.trim() || '');
  let source = scrubCloudCheckoutMarketingCopy(itemsRaw);
  if (!source.trim() && contentRaw?.trim()) {
    const lines = contentRaw.split('\n');
    if (
      lines.some((line) => {
        const t = line.trim();
        return ICON_PREFIX_RE.test(t) || /^@[a-z0-9-]+\b/i.test(t);
      }) ||
      lines.length > 1
    ) {
      source = scrubCloudCheckoutMarketingCopy(contentRaw);
      heading = '';
    }
  }
  if (!source) return { heading, items: [] };
  const hasHideMeta = source.split('\n').some((line) => parseFieldHideMetaLine(line.trim()));
  const useBlocks = source.includes('---') || hasHideMeta;
  const blocks = useBlocks
    ? (source.includes('---') ? splitBlocks(source) : [source])
    : source.split('\n').filter(Boolean);
  const items = blocks.map((line) => {
    const { body, hides } = useBlocks ? parseFieldHideFromBlock(line) : { body: line, hides: {} as Record<string, string> };
    const { icon, text } = parseSiteIconPrefix(body);
    return {
      icon,
      text: scrubCloudCheckoutMarketingCopy(text),
      ...hides,
    };
  });
  return { heading, items };
}

export function serializeIconListItems(heading: string, items: Array<IconListItem & Record<string, string>>): { content: string; items: string } {
  const blocks = items.map((item) => {
    const token = formatSiteIconToken(item.icon);
    return appendFieldHideMeta(`${token} ${item.text}`.trimEnd(), item, ['text']);
  });
  const hasHide = items.some((item) => isEditorFieldHidden(item.textHide));
  if (!hasHide) {
    return { content: heading, items: blocks.join('\n') };
  }
  if (blocks.length === 1) {
    return { content: heading, items: blocks[0] };
  }
  return { content: heading, items: joinBlocks(blocks) };
}

export function parseProgressBarItems(itemsRaw: string, headingRaw?: string, contentRaw?: string): { heading: string; items: ProgressBarItem[] } {
  let heading = headingRaw?.trim() || '';
  let source = itemsRaw.trim();
  if (!source && contentRaw?.includes('|')) {
    source = contentRaw;
    heading = '';
  }
  if (!source) return { heading, items: [] };
  const hasHideMeta = source.split('\n').some((line) => parseFieldHideMetaLine(line.trim()));
  const useBlocks = source.includes('---') || hasHideMeta;
  const blocks = useBlocks
    ? (source.includes('---') ? splitBlocks(source) : [source])
    : source.split('\n').filter(Boolean);
  const items = blocks.map((line) => {
    const { body, hides } = useBlocks ? parseFieldHideFromBlock(line) : { body: line, hides: {} as Record<string, string> };
    const [label, percent] = body.split('|').map((part) => part.trim());
    return { label: label || '', percent: percent || '', ...hides };
  });
  return { heading, items };
}

export function serializeProgressBarItems(heading: string, items: Array<ProgressBarItem & Record<string, string>>): { content: string; items: string } {
  const blocks = items.map((item) => appendFieldHideMeta(`${item.label} | ${item.percent}`, item, ['label', 'percent']));
  const hasHide = items.some(
    (item) => isEditorFieldHidden(item.labelHide) || isEditorFieldHidden(item.percentHide),
  );
  if (!hasHide) {
    return { content: heading, items: blocks.join('\n') };
  }
  if (blocks.length === 1) {
    return { content: heading, items: blocks[0] };
  }
  return { content: heading, items: joinBlocks(blocks) };
}

export function parseBadgeLabels(contentRaw: string): { items: Array<{ label: string; labelHide?: string }> } {
  if (!contentRaw) return { items: [] };
  const hasHideMeta = contentRaw.split('\n').some((line) => parseFieldHideMetaLine(line.trim()));
  const useBlocks = contentRaw.includes('---') || hasHideMeta;
  const blocks = useBlocks
    ? (contentRaw.includes('---') ? splitBlocks(contentRaw) : [contentRaw])
    : contentRaw.split('\n').filter(Boolean);
  const items = blocks.map((line) => {
    const { body, hides } = useBlocks ? parseFieldHideFromBlock(line) : { body: line, hides: {} as Record<string, string> };
    return { label: body.trim(), ...hides };
  });
  return { items };
}

export function serializeBadgeLabels(items: Array<{ label: string; labelHide?: string }>): { content: string } {
  const blocks = items.map((item) => appendFieldHideMeta(item.label, item, ['label']));
  const hasHide = items.some((item) => isEditorFieldHidden(item.labelHide));
  if (!hasHide) {
    return { content: blocks.join('\n') };
  }
  if (blocks.length === 1) {
    return { content: blocks[0] };
  }
  return { content: joinBlocks(blocks) };
}

/**
 * Navbar content format: line 0 = brand name, lines 1+ = "Label | url".
 * Nested submenu rows use 2-space (or tab) indent under their parent.
 * The brand line must stay present even when empty — filtering it out promotes
 * the first link into the brand slot and destroys nav rows mid-edit.
 */
export function parseNavbarContent(contentRaw: string): { brand: string; links: NavLink[] } {
  const lines = (contentRaw ?? '').split('\n');
  // Keep empty brand; strip ZWSP sentinels from older round-trips.
  const brand = (lines[0] ?? '').replace(/\u200B/g, '');
  const source = lines.slice(1).join('\n');
  const hasHideMeta = source.split('\n').some((line) => parseFieldHideMetaLine(line.trim()));
  const useBlocks = source.includes('---') || hasHideMeta;
  if (useBlocks) {
    const blocks = source ? splitBlocks(source) : [];
    const links = blocks.map((block) => parseNavLinkBlock(block));
    return { brand, links };
  }
  // Preserve indent for submenu nesting — do not trim leading whitespace.
  const linkLines = lines.slice(1).filter((line) => line.replace(/\u200B/g, '').trim());
  return { brand, links: buildNavLinkTree(linkLines) };
}

export function serializeNavbarContent(brand: string, links: NavLink[]): { content: string } {
  const visibleLinks = links
    .filter((link) => {
      const children = parseChildrenField(link.children) ?? link.children;
      return link.label.trim() || link.url.trim() || Boolean(children?.length);
    })
    .map((link) => {
      const children = parseChildrenField(link.children);
      const next: NavLink = { ...link, label: link.label || '', url: link.url || '' };
      if (children?.length) next.children = children;
      else delete next.children;
      // Drop editor-only string form so serializeNavLinkBlock uses the tree.
      if (typeof next.children === 'string') {
        const parsed = parseChildrenField(next.children);
        if (parsed?.length) next.children = parsed;
        else delete next.children;
      }
      return next;
    });
  const hasNested = visibleLinks.some((link) => {
    const children = parseChildrenField(link.children) ?? link.children;
    return Boolean(children && (Array.isArray(children) ? children.length : String(children).trim()));
  });
  const hasHide = visibleLinks.some((link) => isEditorFieldHidden(link.labelHide));
  // Preserve brand as line 0 even when empty (`\nHome | /…`) so links never shift up.
  if (!brand.trim() && visibleLinks.length === 0) {
    return { content: 'My Site' };
  }
  // Prefer flat indented lines when there is no hide meta (readable + round-trips).
  // With hide flags, use --- blocks so meta stays attached to the parent.
  let linksContent: string;
  if (!hasHide && !hasNested) {
    linksContent = visibleLinks.map((link) => `${link.label.trim()} | ${link.url.trim()}`.trim()).join('\n');
  } else if (!hasHide) {
    linksContent = flattenNavLinkTree(visibleLinks).join('\n');
  } else {
    const rows = visibleLinks.map((link) => serializeNavLinkBlock(link));
    linksContent = rows.length <= 1 ? rows.join('\n') : joinBlocks(rows);
  }
  return { content: brand ? `${brand}\n${linksContent}` : `\n${linksContent}` };
}

export function parseLinkLines(contentRaw: string): { links: NavLink[] } {
  const raw = contentRaw ?? '';
  const hasHideMeta = raw.split('\n').some((line) => parseFieldHideMetaLine(line.trim()));
  const useBlocks = raw.includes('---') || hasHideMeta;
  const blocks = useBlocks
    ? (raw ? splitBlocks(raw) : [])
    : raw.split('\n').map((line) => line.trim()).filter(Boolean);
  const links = blocks.map((block) => parseNavLinkBlock(block));
  return { links };
}

export function serializeLinkLines(links: NavLink[]): { content: string } {
  const visibleLinks = links
    .filter((link) => link.label.trim() || link.url.trim())
    .map((link) => ({ ...link }));
  const blocks = visibleLinks.map((link) => serializeNavLinkBlock(link));
  const hasHide = visibleLinks.some((link) => isEditorFieldHidden(link.labelHide));
  const content = !hasHide ? blocks.join('\n') : blocks.length <= 1 ? blocks.join('\n') : joinBlocks(blocks);
  return { content };
}

/**
 * Parse one rich-footer column block.
 * Line 0 is heading (may be empty). Never filter(Boolean) before slicing —
 * that promoted the first "Label | /path" row into the column heading.
 * If line 0 itself contains `|`, treat the whole block as link lines (no heading).
 */
export function parseRichFooterColumnBlock(block: string): { heading: string; lines: string[] } {
  const lines = (block || '').split('\n').map((l) => l.replace(/\u200B/g, ''));
  const first = lines[0] ?? '';
  const rest = lines.slice(1);
  const firstTrim = first.trim();
  if (firstTrim.includes('|')) {
    return {
      heading: '',
      lines: [first, ...rest].map((l) => l.trim()).filter(Boolean),
    };
  }
  return {
    heading: firstTrim,
    lines: rest.map((l) => l.trim()).filter(Boolean),
  };
}

/** Display label for a "Label | /path" CMS line (never show the raw pipe form). */
export function footerLinkLabel(line: string): string {
  const parts = line.split('|').map((s) => s.trim());
  return parts[0] || line.trim() || 'Link';
}

/**
 * Copyright / legal lines often use `Privacy | Terms` or `Privacy | /privacy`.
 * Render middot-separated labels so users never see raw authoring pipes.
 */
export function formatFooterCopyright(raw: string): string {
  const text = (raw || '').trim();
  if (!text.includes('|')) return text;
  const parts = text.split('|').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return text.replace(/\s*\|\s*/g, ' · ');

  const labels: string[] = [];
  const pushLabel = (value: string) => {
    const cleaned = value.replace(/\s+(?:\/|#)\S*$/, '').trim();
    if (cleaned) labels.push(cleaned);
  };

  for (const part of parts) {
    if (/^(?:\/|#|https?:|mailto:|tel:)/i.test(part)) {
      for (const bit of part.split('·').map((s) => s.trim()).filter(Boolean)) {
        if (/^(?:\/|#|https?:|mailto:|tel:)/i.test(bit)) continue;
        pushLabel(bit);
      }
      continue;
    }
    for (const bit of part.split('·').map((s) => s.trim()).filter(Boolean)) {
      if (/^(?:\/|#|https?:|mailto:|tel:)/i.test(bit)) continue;
      pushLabel(bit);
    }
  }
  if (labels.length === 0) return text.replace(/\s*\|\s*/g, ' · ');
  return labels.join(' · ');
}

export function parseRichFooterColumns(contentRaw: string): { columns: Array<FooterColumn & Record<string, string>> } {
  const blocks = splitBlocks(contentRaw);
  const columns = blocks.map((block) => {
    const { body, hides } = parseFieldHideFromBlock(block);
    const { heading, lines } = parseRichFooterColumnBlock(body);
    return {
      columnHeading: heading,
      columnLines: lines.join('\n'),
      ...hides,
    };
  });
  return { columns };
}

export function serializeRichFooterColumns(columns: Array<FooterColumn & Record<string, string>>): { content: string } {
  const content = joinBlocks(
    columns.map((column) =>
      appendFieldHideMeta(`${column.columnHeading}\n${column.columnLines}`, column, ['columnHeading', 'columnLines']),
    ),
  );
  return { content };
}

export function parseLogoUrls(contentRaw: string): { urls: Array<{ url: string }> } {
  const urls = contentRaw.split('\n').map((line) => line.trim()).filter(Boolean).map((url) => ({ url }));
  return { urls };
}

export function serializeLogoUrls(urls: Array<{ url: string }>): { content: string } {
  const content = urls.map((item) => item.url.trim()).filter(Boolean).join('\n');
  return { content };
}

/** Per-slide caption placement for Home Slider / carousel overlays. */
export const CAROUSEL_CAPTION_POSITIONS = [
  'left',
  'center',
  'right',
  'top-left',
  'top-center',
  'top-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
  'hide',
] as const;

export type CarouselCaptionPosition = (typeof CAROUSEL_CAPTION_POSITIONS)[number];

const CAPTION_POS_SET = new Set<string>(CAROUSEL_CAPTION_POSITIONS);
const CAPTION_POS_PREFIX = /^@pos:\s*/i;

/** Normalize raw editor / serialized caption position (empty → ''). */
export function normalizeCarouselCaptionPosition(raw: unknown): CarouselCaptionPosition | '' {
  if (typeof raw !== 'string') return '';
  const v = raw.trim().toLowerCase().replace(CAPTION_POS_PREFIX, '').replace(/_/g, '-');
  if (!v || v === 'default') return '';
  return CAPTION_POS_SET.has(v) ? (v as CarouselCaptionPosition) : '';
}

/** BEM modifier class for `.site-carousel__caption` (defaults to center). */
export function carouselCaptionPositionClass(
  position: string | undefined | null,
  fallback?: string | null,
): string {
  const pos =
    normalizeCarouselCaptionPosition(position) ||
    normalizeCarouselCaptionPosition(fallback) ||
    'center';
  if (pos === 'hide') return 'site-carousel__caption--hidden';
  return `site-carousel__caption--${pos}`;
}

/** Home Slider frame height presets (width stays full / auto). */
export const CAROUSEL_HEIGHT_PRESETS = [
  'default',
  'large',
  'medium',
  'small',
  'custom',
] as const;

export type CarouselHeightPreset = (typeof CAROUSEL_HEIGHT_PRESETS)[number];

const HEIGHT_PRESET_SET = new Set<string>(CAROUSEL_HEIGHT_PRESETS);

/** Normalize editor / stored height preset (unknown → 'default'). */
export function normalizeCarouselHeightPreset(raw: unknown): CarouselHeightPreset {
  if (typeof raw !== 'string') return 'default';
  const v = raw.trim().toLowerCase();
  return HEIGHT_PRESET_SET.has(v) ? (v as CarouselHeightPreset) : 'default';
}

/**
 * Vertical focal point when cover/width-fit crops the slide.
 * Default `top` keeps faces/heads visible on people photography (center crop was
 * chopping foreheads on Home Slider heroes).
 */
export const CAROUSEL_IMAGE_OBJECT_POSITIONS = ['top', 'center', 'bottom'] as const;
export type CarouselImageObjectPosition = (typeof CAROUSEL_IMAGE_OBJECT_POSITIONS)[number];

const IMAGE_OBJECT_POSITION_SET = new Set<string>(CAROUSEL_IMAGE_OBJECT_POSITIONS);

export function normalizeCarouselImageObjectPosition(raw: unknown): CarouselImageObjectPosition {
  if (typeof raw !== 'string') return 'top';
  const v = raw.trim().toLowerCase();
  return IMAGE_OBJECT_POSITION_SET.has(v) ? (v as CarouselImageObjectPosition) : 'top';
}

export function carouselImageObjectPositionClass(position: unknown): string {
  return `site-carousel--focus-${normalizeCarouselImageObjectPosition(position)}`;
}

/**
 * Sanitize a custom CSS length for carousel height.
 * Allows only a single positive number with unit px|vh|rem|% — no calc/expr/url.
 */
export function sanitizeCarouselHeightCustom(raw: unknown, maxLen = 24): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim().slice(0, maxLen);
  if (!trimmed) return undefined;
  // Reject anything that looks like an expression or multi-token value.
  if (/[;{}()\\]|calc|var|url|expression|attr|env|min|max|clamp|!important/i.test(trimmed)) {
    return undefined;
  }
  const m = /^(\d+(?:\.\d+)?)(px|vh|rem|%)$/i.exec(trimmed);
  if (!m) return undefined;
  const num = Number(m[1]);
  if (!Number.isFinite(num) || num <= 0) return undefined;
  // Cap extreme values that would break layout (e.g. 99999px / 500vh).
  const unit = m[2].toLowerCase();
  if (unit === 'px' && num > 2000) return undefined;
  if (unit === 'vh' && num > 100) return undefined;
  if (unit === 'rem' && num > 80) return undefined;
  if (unit === '%' && num > 100) return undefined;
  return `${m[1]}${unit}`;
}

/** BEM height modifier for `.site-carousel` (always includes a height-* class). */
export function carouselHeightClass(preset: unknown): string {
  return `site-carousel--height-${normalizeCarouselHeightPreset(preset)}`;
}

/**
 * Parse a sanitized custom height length to CSS pixels (viewport-relative units need `viewportHeight`).
 * Returns undefined when the value cannot be resolved to a finite positive px length.
 */
export function carouselHeightCustomToPx(
  heightCustom: string | undefined,
  viewportHeight = 800,
  rootFontPx = 16,
): number | undefined {
  if (!heightCustom) return undefined;
  const m = /^(\d+(?:\.\d+)?)(px|vh|rem|%)$/i.exec(heightCustom.trim());
  if (!m) return undefined;
  const num = Number(m[1]);
  if (!Number.isFinite(num) || num <= 0) return undefined;
  const unit = m[2].toLowerCase();
  if (unit === 'px') return num;
  if (unit === 'vh') return (num / 100) * viewportHeight;
  if (unit === 'rem') return num * rootFontPx;
  // % of viewport height — carousel frames are viewport-anchored heroes more often than parents.
  if (unit === '%') return (num / 100) * viewportHeight;
  return undefined;
}

/**
 * Max frame height (px) for `imageFit: width` AR sizing.
 * Named Slider-Höhe presets (Klein/Mittel/Groß/Anpassen) act as a **cap** on
 * image-driven height (not a rigid taller box). Matches the upper bound of the
 * corresponding CSS `clamp(...)` presets.
 *
 * **Standard (`default`)** is uncapped: frame height follows image AR at full
 * width — no max-height crop path and therefore no soft blur fill. Named
 * presets cap height only; media stays full-width (`cover` when capped).
 */
export function carouselWidthFitMaxHeightPx(opts: {
  preset: CarouselHeightPreset | string;
  heightCustom?: string;
  heroMode?: boolean;
  viewportHeight?: number;
}): number {
  const preset = normalizeCarouselHeightPreset(opts.preset);
  // Standard: never cap — stable full-width fit, height = image AR.
  if (preset === 'default') return Number.POSITIVE_INFINITY;
  const vh = opts.viewportHeight ?? (typeof window !== 'undefined' ? window.innerHeight : 800);
  const globalCap = Math.min(vh * 0.78, 920);
  if (preset === 'custom') {
    const customPx = carouselHeightCustomToPx(opts.heightCustom, vh);
    return customPx != null ? Math.min(customPx, globalCap) : globalCap;
  }
  const hero = Boolean(opts.heroMode);
  const namedMax =
    preset === 'large'
      ? hero
        ? 900
        : 520
      : preset === 'medium'
        ? hero
          ? 520
          : 340
        : preset === 'small'
          ? hero
            ? 360
            : 260
          : globalCap;
  return Math.min(namedMax, globalCap);
}

/**
 * Narrow carousel band (phones + tablets + editor Mobil/Tablet canvas).
 * Used for AR frame height + controls-below-frame — prefer measuring the
 * carousel element's width over `window.matchMedia` so Freigabe + editor agree.
 */
export const CAROUSEL_NARROW_MAX_PX = 960;

export function carouselIsNarrowWidth(widthPx: number): boolean {
  return Number.isFinite(widthPx) && widthPx > 0 && widthPx <= CAROUSEL_NARROW_MAX_PX;
}

/**
 * Phone / narrow hero frames: keep enough height for title + subtitle + CTA.
 * Wide landscape motifs otherwise collapse to ~120–160px via AR width-fit and
 * crush caption typography. Cover-crop absorbs the extra height.
 */
export function carouselMobileHeroMinHeightPx(opts?: {
  viewportHeight?: number;
  viewportWidth?: number;
}): number {
  const vh = opts?.viewportHeight ?? (typeof window !== 'undefined' ? window.innerHeight : 800);
  const vw = opts?.viewportWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 390);
  // Phones: enough room for 2–3 title lines + CTA. Tablets (wider vw): higher
  // floor so captions stay readable without filling half the screen.
  const fromVh = Math.round(vh * 0.4);
  const fromVw = Math.round(vw * (vw >= 700 ? 0.48 : 0.62));
  const floor = vw >= 700 ? 260 : 230;
  const ceiling = vw >= 700 ? 420 : 340;
  return Math.min(ceiling, Math.max(floor, Math.min(fromVh, fromVw)));
}

/**
 * Home Slider slide transition presets (`props.slideAnimation`).
 * `default` preserves the historic opacity fade (pre-animation-control behavior).
 */
export const CAROUSEL_SLIDE_ANIMATIONS = [
  'default',
  'slide-in',
  'slide-out',
  'slide-in-left',
  'slide-in-right',
  'slide-in-top',
  'slide-in-bottom',
  'slide-out-left',
  'slide-out-right',
  'slide-out-top',
  'slide-out-bottom',
  'zoom-in',
  'zoom-out',
  'fade-slide-up',
  'fade-slide-down',
  'ken-burns',
  'flip-x',
  'flip-y',
  'rotate-in',
  'blur-fade',
  'coverflow',
  'cube',
  'wipe-right',
  'soft-rise',
  'swirl',
] as const;

export type CarouselSlideAnimation = (typeof CAROUSEL_SLIDE_ANIMATIONS)[number];

const SLIDE_ANIM_SET = new Set<string>(CAROUSEL_SLIDE_ANIMATIONS);

/** German / legacy labels sometimes stored instead of canonical ids. */
const SLIDE_ANIM_ALIASES: Record<string, CarouselSlideAnimation> = {
  wuerfel: 'cube',
  wurfel: 'cube',
  würfel: 'cube',
  standard: 'default',
};

/** Normalize editor / stored slide animation (unknown → 'default'). */
export function normalizeCarouselSlideAnimation(raw: unknown): CarouselSlideAnimation {
  if (typeof raw !== 'string') return 'default';
  const v = raw.trim().toLowerCase().replace(/_/g, '-');
  if (!v || v === 'none' || v === 'fade') return 'default';
  const aliased = SLIDE_ANIM_ALIASES[v];
  if (aliased) return aliased;
  return SLIDE_ANIM_SET.has(v) ? (v as CarouselSlideAnimation) : 'default';
}

/** BEM animation modifier for `.site-carousel` (always includes an anim-* class). */
export function carouselSlideAnimationClass(animation: unknown): string {
  return `site-carousel--anim-${normalizeCarouselSlideAnimation(animation)}`;
}

/** True when the animation benefits from an explicit exiting-slide class. */
export function carouselSlideAnimationNeedsExit(animation: unknown): boolean {
  // All presets including `default` use enter/exit so forward/back can reverse travel.
  void animation;
  return true;
}

/**
 * CSS enter/exit duration for a slide animation (ms).
 * Used to keep the exiting-slide class painted until keyframes finish.
 */
export function carouselSlideAnimationDurationMs(animation: unknown): number {
  const anim = normalizeCarouselSlideAnimation(animation);
  switch (anim) {
    case 'wipe-right':
      return 750;
    case 'blur-fade':
    case 'ken-burns':
    case 'flip-x':
    case 'flip-y':
    case 'coverflow':
    case 'cube':
    case 'rotate-in':
    case 'swirl':
      return 700;
    case 'default':
    default:
      return 650;
  }
}

/** Forward = next / autoplay; backward = previous. Used for logical slide directions. */
export type CarouselNavDirection = 'forward' | 'backward';

/**
 * Shortest wrap-aware step from `from` → `to`.
 * Ties (e.g. 2 slides flipping) prefer forward when `to === (from + 1) % total`.
 */
export function resolveCarouselNavDirection(
  from: number,
  to: number,
  total: number,
): CarouselNavDirection {
  if (total <= 1) return 'forward';
  const f = ((from % total) + total) % total;
  const t = ((to % total) + total) % total;
  if (f === t) return 'forward';
  const forwardDist = (t - f + total) % total;
  const backwardDist = (f - t + total) % total;
  if (forwardDist < backwardDist) return 'forward';
  if (backwardDist < forwardDist) return 'backward';
  // Equal distance (even totals): treat step +1 as forward.
  return t === (f + 1) % total ? 'forward' : 'backward';
}

/** BEM direction modifier for `.site-carousel` (always present). */
export function carouselNavDirectionClass(dir: CarouselNavDirection | unknown): string {
  return dir === 'backward' ? 'site-carousel--dir-backward' : 'site-carousel--dir-forward';
}

/**
 * Default Home Slider image overlay — matches the historic dark caption wash
 * (~rgba black at ~0.4). Templates without overlay props inherit these via CSS.
 */
export const DEFAULT_CAROUSEL_OVERLAY_COLOR = '#000000';
export const DEFAULT_CAROUSEL_OVERLAY_OPACITY = 0.4;

const OVERLAY_COLOR_PREFIX = /^@overlayColor:\s*/i;
const OVERLAY_OPACITY_PREFIX = /^@overlayOpacity:\s*/i;
const TITLE_COLOR_PREFIX = /^@titleColor:\s*/i;
const TITLE_GRADIENT_PREFIX = /^@titleGradient:\s*/i;
const TITLE_FONT_SIZE_PREFIX = /^@titleFontSize:\s*/i;
const SUBTITLE_COLOR_PREFIX = /^@subtitleColor:\s*/i;
const SUBTITLE_FONT_SIZE_PREFIX = /^@subtitleFontSize:\s*/i;
const BUTTON_COLOR_PREFIX = /^@buttonColor:\s*/i;
const BUTTON_BG_PREFIX = /^@buttonBg:\s*/i;
const BUTTON_GRADIENT_PREFIX = /^@buttonGradient:\s*/i;
const BUTTON_FONT_SIZE_PREFIX = /^@buttonFontSize:\s*/i;

/** Sanitize overlay / caption solid color to `#rrggbb` (empty / invalid → undefined). */
export function sanitizeCarouselOverlayColor(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const v = raw
    .trim()
    .replace(OVERLAY_COLOR_PREFIX, '')
    .replace(TITLE_COLOR_PREFIX, '')
    .replace(SUBTITLE_COLOR_PREFIX, '')
    .replace(BUTTON_COLOR_PREFIX, '')
    .replace(BUTTON_BG_PREFIX, '');
  if (!v) return undefined;
  if (/^#[0-9a-f]{6}$/i.test(v)) return v.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(v)) {
    const r = v[1];
    const g = v[2];
    const b = v[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return undefined;
}

/** Sanitize caption/button gradient or CSS paint (empty / invalid → undefined). */
export function sanitizeCarouselCaptionPaint(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const v = raw
    .trim()
    .replace(TITLE_GRADIENT_PREFIX, '')
    .replace(BUTTON_GRADIENT_PREFIX, '')
    .replace(BUTTON_BG_PREFIX, '');
  if (!v) return undefined;
  return sanitizeCssPaintValue(v, 240);
}

/** Sanitize caption font size in px (8–200). Empty / invalid → undefined (theme default). */
export function sanitizeCarouselFontSizePx(raw: unknown): number | undefined {
  if (raw === null || raw === undefined) return undefined;
  let n: number;
  if (typeof raw === 'number') {
    n = raw;
  } else if (typeof raw === 'string') {
    const trimmed = raw
      .trim()
      .replace(TITLE_FONT_SIZE_PREFIX, '')
      .replace(SUBTITLE_FONT_SIZE_PREFIX, '')
      .replace(BUTTON_FONT_SIZE_PREFIX, '')
      .replace(/px$/i, '')
      .trim();
    if (!trimmed) return undefined;
    n = Number(trimmed);
  } else {
    return undefined;
  }
  if (!Number.isFinite(n) || n < 8 || n > 200) return undefined;
  return Math.round(n);
}

/**
 * Sanitize overlay opacity to 0–1.
 * Accepts `0`–`1`, `0%`–`100%`, or `0`–`100` (treated as percent when > 1).
 * Empty / invalid → undefined (inherit block default / CSS default).
 */
export function sanitizeCarouselOverlayOpacity(raw: unknown): number | undefined {
  if (raw === null || raw === undefined) return undefined;
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return undefined;
    if (raw < 0) return undefined;
    if (raw > 1 && raw <= 100) return Math.round(raw) / 100;
    if (raw > 100) return undefined;
    return Math.round(raw * 1000) / 1000;
  }
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim().replace(OVERLAY_OPACITY_PREFIX, '');
  if (!trimmed) return undefined;
  const asPct = trimmed.endsWith('%');
  const num = Number(trimmed.replace(/%$/, ''));
  if (!Number.isFinite(num) || num < 0) return undefined;
  if (asPct || (num > 1 && num <= 100)) {
    if (num > 100) return undefined;
    return Math.round(num) / 100;
  }
  if (num > 1) return undefined;
  return Math.round(num * 1000) / 1000;
}

/** Resolve effective overlay color (slide → block → platform default). */
export function resolveCarouselOverlayColor(
  slideRaw: unknown,
  blockRaw: unknown,
): string {
  return (
    sanitizeCarouselOverlayColor(slideRaw) ||
    sanitizeCarouselOverlayColor(blockRaw) ||
    DEFAULT_CAROUSEL_OVERLAY_COLOR
  );
}

/** Resolve effective overlay opacity 0–1 (slide → block → platform default). */
export function resolveCarouselOverlayOpacity(
  slideRaw: unknown,
  blockRaw: unknown,
): number {
  const fromSlide = sanitizeCarouselOverlayOpacity(slideRaw);
  if (fromSlide !== undefined) return fromSlide;
  const fromBlock = sanitizeCarouselOverlayOpacity(blockRaw);
  if (fromBlock !== undefined) return fromBlock;
  return DEFAULT_CAROUSEL_OVERLAY_OPACITY;
}

export interface CarouselSlide {
  imageUrl: string;
  heading: string;
  subtitle: string;
  ctaLabel: string;
  ctaLink: string;
  /** Legacy plain-text-only slide (no image) — see SiteRenderer.tsx's CarouselBlock. */
  text: string;
  /** Optional overlay placement; empty inherits block `props.captionPosition` or center. */
  captionPosition?: string;
  /** Optional per-slide overlay color (`#rrggbb`); empty inherits block `slideOverlayColor`. */
  overlayColor?: string;
  /** Optional per-slide overlay opacity (0–1 string); empty inherits block `slideOverlayOpacity`. */
  overlayOpacity?: string;
  /** Optional slide heading solid color (`#rrggbb`); empty → theme/white caption look. */
  titleColor?: string;
  /** Optional slide heading gradient; when set, takes precedence over `titleColor`. */
  titleGradient?: string;
  /** Optional slide heading font size in px (8–200); empty → CSS clamp default. */
  titleFontSize?: string;
  /** Optional slide subtitle solid color. */
  subtitleColor?: string;
  /** Optional slide subtitle font size in px. */
  subtitleFontSize?: string;
  /** Optional CTA button text color. */
  buttonColor?: string;
  /** Optional CTA button solid background. */
  buttonBg?: string;
  /** Optional CTA button gradient background; takes precedence over `buttonBg`. */
  buttonGradient?: string;
  /** Optional CTA button font size in px. */
  buttonFontSize?: string;
  /** When `true`, keep heading text but hide on the public site. */
  headingHide?: string;
  subtitleHide?: string;
  ctaLabelHide?: string;
  textHide?: string;
  /** When `true`, omit the entire slide (media + caption + dots) on the public site. */
  slideHide?: string;
}

/** Empty caption-style fields used by legacy text-only slides and fresh parses. */
function emptyCarouselCaptionStyles(): Pick<
  CarouselSlide,
  | 'titleColor'
  | 'titleGradient'
  | 'titleFontSize'
  | 'subtitleColor'
  | 'subtitleFontSize'
  | 'buttonColor'
  | 'buttonBg'
  | 'buttonGradient'
  | 'buttonFontSize'
> {
  return {
    titleColor: '',
    titleGradient: '',
    titleFontSize: '',
    subtitleColor: '',
    subtitleFontSize: '',
    buttonColor: '',
    buttonBg: '',
    buttonGradient: '',
    buttonFontSize: '',
  };
}

/** Inline styles for slide heading — only dynamic values (unset → CSS defaults).
 * Font size is stored as a CSS variable only so mobile media queries can hard-cap
 * with !important without fighting an inline `fontSize` declaration. */
export function carouselTitleStyle(slide: {
  titleColor?: string;
  titleGradient?: string;
  titleFontSize?: string;
}): Record<string, string> | undefined {
  const style: Record<string, string> = {};
  const gradient = sanitizeCarouselCaptionPaint(slide.titleGradient);
  const color = sanitizeCarouselOverlayColor(slide.titleColor);
  const size = sanitizeCarouselFontSizePx(slide.titleFontSize);
  if (gradient) {
    style.backgroundImage = gradient;
    style.WebkitBackgroundClip = 'text';
    style.backgroundClip = 'text';
    style.color = 'transparent';
  } else if (color) {
    style.color = color;
  }
  if (size !== undefined) {
    style['--carousel-title-size'] = `${size}px`;
  }
  return Object.keys(style).length ? style : undefined;
}

/** Inline styles for slide subtitle. */
export function carouselSubtitleStyle(slide: {
  subtitleColor?: string;
  subtitleFontSize?: string;
}): Record<string, string> | undefined {
  const style: Record<string, string> = {};
  const color = sanitizeCarouselOverlayColor(slide.subtitleColor);
  const size = sanitizeCarouselFontSizePx(slide.subtitleFontSize);
  if (color) style.color = color;
  if (size !== undefined) {
    style['--carousel-subtitle-size'] = `${size}px`;
  }
  return Object.keys(style).length ? style : undefined;
}

/** Inline styles for slide CTA button (text + background). */
export function carouselButtonStyle(slide: {
  buttonColor?: string;
  buttonBg?: string;
  buttonGradient?: string;
  buttonFontSize?: string;
}): Record<string, string> | undefined {
  const style: Record<string, string> = {};
  const textColor = sanitizeCarouselOverlayColor(slide.buttonColor);
  const gradient = sanitizeCarouselCaptionPaint(slide.buttonGradient);
  const bg = sanitizeCarouselOverlayColor(slide.buttonBg) || sanitizeCarouselCaptionPaint(slide.buttonBg);
  const size = sanitizeCarouselFontSizePx(slide.buttonFontSize);
  if (textColor) style.color = textColor;
  if (gradient) style.background = gradient;
  else if (bg) style.background = bg;
  if (size !== undefined) {
    style['--carousel-button-size'] = `${size}px`;
  }
  return Object.keys(style).length ? style : undefined;
}

/** A slide block supports two formats (mirrors SiteRenderer.tsx's `parseCarouselSlide`,
 * which this must stay in sync with): a single line of plain text (legacy, rendered as
 * a centered text slide), or an image URL optionally followed by heading/subtitle/CTA
 * caption lines: `imageUrl\nheading\nsubtitle\nctaLabel | ctaLink` and optional
 * `@pos:top-left` (or bare known token) for caption placement, plus optional
 * `@overlayColor:#rrggbb` / `@overlayOpacity:0.4` for per-slide image wash, and optional
 * caption typography meta (`@titleColor`, `@titleGradient`, `@titleFontSize`, …).
 * Only leading/trailing blank lines are trimmed — an interior blank (e.g. a slide
 * with a CTA but no subtitle) must keep its position so heading/subtitle/CTA
 * don't shift up a slot. */
export function parseCarouselSlideBlock(block: string): CarouselSlide {
  const lines = block.split('\n').map((l) => l.trim());
  while (lines.length && !lines[0]) lines.shift();
  while (lines.length && !lines[lines.length - 1]) lines.pop();
  const first = lines[0] || '';
  // Image URLs, SVG data-URLs, and CSS solid/gradient backgrounds all count as slide media.
  if (!isCarouselSlideMedia(first)) {
    const { body, hides } = parseFieldHideFromBlock(block);
    return {
      imageUrl: '',
      heading: '',
      subtitle: '',
      ctaLabel: '',
      ctaLink: '',
      text: body.trim(),
      captionPosition: '',
      overlayColor: '',
      overlayOpacity: '',
      textHide: hides.textHide || '',
      slideHide: hides.slideHide || '',
      ...emptyCarouselCaptionStyles(),
    };
  }

  let captionPosition = '';
  let overlayColor = '';
  let overlayOpacity = '';
  let headingHide = '';
  let subtitleHide = '';
  let ctaLabelHide = '';
  let textHide = '';
  let slideHide = '';
  const caption = emptyCarouselCaptionStyles();
  const body = [...lines];

  // Pull optional overlay / caption meta lines (anywhere in the block).
  for (let i = body.length - 1; i >= 0; i--) {
    const line = body[i];
    const hideMeta = parseFieldHideMetaLine(line);
    if (hideMeta) {
      if (hideMeta.field === 'heading') headingHide = 'true';
      else if (hideMeta.field === 'subtitle') subtitleHide = 'true';
      else if (hideMeta.field === 'ctaLabel') ctaLabelHide = 'true';
      else if (hideMeta.field === 'text') textHide = 'true';
      else if (hideMeta.field === 'slide') slideHide = 'true';
      body.splice(i, 1);
      continue;
    }
    if (OVERLAY_COLOR_PREFIX.test(line)) {
      const color = sanitizeCarouselOverlayColor(line);
      if (color) overlayColor = color;
      body.splice(i, 1);
      continue;
    }
    if (OVERLAY_OPACITY_PREFIX.test(line)) {
      const op = sanitizeCarouselOverlayOpacity(line);
      if (op !== undefined) overlayOpacity = String(op);
      body.splice(i, 1);
      continue;
    }
    if (TITLE_COLOR_PREFIX.test(line)) {
      const color = sanitizeCarouselOverlayColor(line.replace(TITLE_COLOR_PREFIX, '').trim());
      if (color) caption.titleColor = color;
      body.splice(i, 1);
      continue;
    }
    if (TITLE_GRADIENT_PREFIX.test(line)) {
      const paint = sanitizeCarouselCaptionPaint(line);
      if (paint) caption.titleGradient = paint;
      body.splice(i, 1);
      continue;
    }
    if (TITLE_FONT_SIZE_PREFIX.test(line)) {
      const size = sanitizeCarouselFontSizePx(line);
      if (size !== undefined) caption.titleFontSize = String(size);
      body.splice(i, 1);
      continue;
    }
    if (SUBTITLE_COLOR_PREFIX.test(line)) {
      const color = sanitizeCarouselOverlayColor(line.replace(SUBTITLE_COLOR_PREFIX, '').trim());
      if (color) caption.subtitleColor = color;
      body.splice(i, 1);
      continue;
    }
    if (SUBTITLE_FONT_SIZE_PREFIX.test(line)) {
      const size = sanitizeCarouselFontSizePx(line);
      if (size !== undefined) caption.subtitleFontSize = String(size);
      body.splice(i, 1);
      continue;
    }
    if (BUTTON_COLOR_PREFIX.test(line)) {
      const color = sanitizeCarouselOverlayColor(line.replace(BUTTON_COLOR_PREFIX, '').trim());
      if (color) caption.buttonColor = color;
      body.splice(i, 1);
      continue;
    }
    if (BUTTON_BG_PREFIX.test(line)) {
      const solid = sanitizeCarouselOverlayColor(line.replace(BUTTON_BG_PREFIX, '').trim());
      const paint = solid || sanitizeCarouselCaptionPaint(line);
      if (paint) caption.buttonBg = paint;
      body.splice(i, 1);
      continue;
    }
    if (BUTTON_GRADIENT_PREFIX.test(line)) {
      const paint = sanitizeCarouselCaptionPaint(line);
      if (paint) caption.buttonGradient = paint;
      body.splice(i, 1);
      continue;
    }
    if (BUTTON_FONT_SIZE_PREFIX.test(line)) {
      const size = sanitizeCarouselFontSizePx(line);
      if (size !== undefined) caption.buttonFontSize = String(size);
      body.splice(i, 1);
    }
  }

  // Prefer explicit `@pos:…` anywhere; also accept a trailing bare known token.
  const atIdx = body.findIndex((l) => CAPTION_POS_PREFIX.test(l));
  if (atIdx >= 0) {
    captionPosition = normalizeCarouselCaptionPosition(body[atIdx]);
    body.splice(atIdx, 1);
  } else if (body.length > 4) {
    const last = normalizeCarouselCaptionPosition(body[body.length - 1]);
    if (last) {
      captionPosition = last;
      body.pop();
    }
  }
  // CTA slot may be a bare position token when no CTA is set (`…\nhide`).
  if (!captionPosition && body.length >= 4) {
    const maybePos = normalizeCarouselCaptionPosition(body[3]);
    if (maybePos && !body[3].includes('|')) {
      captionPosition = maybePos;
      body.splice(3, 1);
    }
  }

  const [ctaLabel, ctaLink] = (body[3] || '').split('|').map((s) => s.trim());
  return {
    imageUrl: first,
    heading: body[1] || '',
    subtitle: body[2] || '',
    ctaLabel: ctaLabel || '',
    ctaLink: ctaLink || '',
    text: '',
    captionPosition,
    overlayColor,
    overlayOpacity,
    headingHide,
    subtitleHide,
    ctaLabelHide,
    textHide,
    slideHide,
    ...caption,
  };
}

export function serializeCarouselSlideBlock(slide: CarouselSlide): string {
  if (!slide.imageUrl.trim()) {
    const lines: string[] = [];
    if (slide.text.trim()) lines.push(slide.text.trim());
    if (isEditorFieldHidden(slide.slideHide)) lines.push('@slideHide:true');
    if (isEditorFieldHidden(slide.textHide)) lines.push('@textHide:true');
    return lines.join('\n');
  }
  const ctaLine = slide.ctaLabel.trim() ? `${slide.ctaLabel.trim()} | ${slide.ctaLink.trim() || '#'}` : '';
  const lines = [slide.imageUrl.trim(), slide.heading.trim(), slide.subtitle.trim(), ctaLine];
  while (lines.length > 1 && !lines[lines.length - 1]) lines.pop();
  const pos = normalizeCarouselCaptionPosition(slide.captionPosition);
  if (pos) lines.push(`@pos:${pos}`);
  const color = sanitizeCarouselOverlayColor(slide.overlayColor);
  if (color) lines.push(`@overlayColor:${color}`);
  const opacity = sanitizeCarouselOverlayOpacity(slide.overlayOpacity);
  if (opacity !== undefined) lines.push(`@overlayOpacity:${opacity}`);
  const titleColor = sanitizeCarouselOverlayColor(slide.titleColor);
  if (titleColor) lines.push(`@titleColor:${titleColor}`);
  const titleGradient = sanitizeCarouselCaptionPaint(slide.titleGradient);
  if (titleGradient) lines.push(`@titleGradient:${titleGradient}`);
  const titleFontSize = sanitizeCarouselFontSizePx(slide.titleFontSize);
  if (titleFontSize !== undefined) lines.push(`@titleFontSize:${titleFontSize}`);
  const subtitleColor = sanitizeCarouselOverlayColor(slide.subtitleColor);
  if (subtitleColor) lines.push(`@subtitleColor:${subtitleColor}`);
  const subtitleFontSize = sanitizeCarouselFontSizePx(slide.subtitleFontSize);
  if (subtitleFontSize !== undefined) lines.push(`@subtitleFontSize:${subtitleFontSize}`);
  const buttonColor = sanitizeCarouselOverlayColor(slide.buttonColor);
  if (buttonColor) lines.push(`@buttonColor:${buttonColor}`);
  const buttonBg =
    sanitizeCarouselOverlayColor(slide.buttonBg) || sanitizeCarouselCaptionPaint(slide.buttonBg);
  if (buttonBg) lines.push(`@buttonBg:${buttonBg}`);
  const buttonGradient = sanitizeCarouselCaptionPaint(slide.buttonGradient);
  if (buttonGradient) lines.push(`@buttonGradient:${buttonGradient}`);
  const buttonFontSize = sanitizeCarouselFontSizePx(slide.buttonFontSize);
  if (buttonFontSize !== undefined) lines.push(`@buttonFontSize:${buttonFontSize}`);
  if (isEditorFieldHidden(slide.slideHide)) lines.push('@slideHide:true');
  if (isEditorFieldHidden(slide.headingHide)) lines.push('@headingHide:true');
  if (isEditorFieldHidden(slide.subtitleHide)) lines.push('@subtitleHide:true');
  if (isEditorFieldHidden(slide.ctaLabelHide)) lines.push('@ctaLabelHide:true');
  if (isEditorFieldHidden(slide.textHide)) lines.push('@textHide:true');
  return lines.join('\n');
}

export function parseCarouselSlides(contentRaw: string): { slides: CarouselSlide[] } {
  const blocks = splitBlocks(contentRaw);
  return { slides: blocks.map(parseCarouselSlideBlock) };
}

export function serializeCarouselSlides(slides: CarouselSlide[]): { content: string } {
  const content = joinBlocks(slides.map(serializeCarouselSlideBlock));
  return { content };
}

function testimonialQuoteBlockBody(item: TestimonialQuote & { imageUrl?: string }): string {
  const lines: string[] = [];
  if ((item.imageUrl || '').trim()) lines.push(item.imageUrl!.trim());
  lines.push(item.quote);
  return lines.join('\n');
}

function parseTestimonialQuoteBlock(block: string): TestimonialQuote {
  const { body, hides } = parseFieldHideFromBlock(block);
  const lines = body.split('\n');
  let imageUrl = '';
  let offset = 0;
  const first = (lines[0] || '').trim();
  if (first && isCarouselSlideMedia(first) && isImageBackgroundValue(first)) {
    imageUrl = first;
    offset = 1;
  }
  const quote = lines.slice(offset).join('\n').trim();
  return { quote, imageUrl, ...hides };
}

export function parseTestimonialQuotes(quotesRaw: string, headingRaw?: string, contentRaw?: string): { heading: string; quotes: TestimonialQuote[] } {
  let source = quotesRaw.trim();
  let heading = headingRaw?.trim() || '';
  if (!source && contentRaw?.trim()) {
    const blocks = splitBlocks(contentRaw);
    if (blocks.length > 0) {
      source = contentRaw;
      heading = heading || 'Testimonials';
    }
  }
  const quotes = splitBlocks(source).map(parseTestimonialQuoteBlock);
  return { heading: heading || 'Testimonials', quotes };
}

export function serializeTestimonialQuotes(heading: string, quotes: Array<TestimonialQuote & Record<string, string>>): { content: string; quotes: string } {
  const serialized = joinBlocks(
    quotes.map((item) => appendFieldHideMeta(testimonialQuoteBlockBody(item), item, ['quote'])),
  );
  return { content: heading.trim() || 'Testimonials', quotes: serialized };
}

export function parseTestimonialSliderQuotes(contentRaw: string, quotesRaw?: string): { quotes: TestimonialQuote[] } {
  const source = quotesRaw?.trim() || contentRaw.trim();
  const quotes = splitBlocks(source).map(parseTestimonialQuoteBlock);
  return { quotes };
}

export function serializeTestimonialSliderQuotes(quotes: Array<TestimonialQuote & Record<string, string>>): { content: string } {
  const content = joinBlocks(
    quotes.map((item) => appendFieldHideMeta(testimonialQuoteBlockBody(item), item, ['quote'])),
  );
  return { content };
}

export function parsePricingTogglePlans(plansRaw: string, headingRaw?: string, contentRaw?: string): { heading: string; plans: Array<PricingTogglePlan & Record<string, string>> } {
  let source = plansRaw.trim();
  let heading = headingRaw?.trim() || '';
  if (!source && contentRaw?.includes('/')) {
    source = contentRaw;
    heading = '';
  }
  const plans = splitBlocks(source).map((block) => {
    const { body, hides } = parseFieldHideFromBlock(block);
    const lines = body.split('\n').map((line) => line.trim());
    const [name, prices, ...featureLines] = lines;
    const [monthlyPrice, annualPrice] = (prices || '').split('/').map((part) => part.trim());
    return {
      name: name || '',
      monthlyPrice: monthlyPrice || '',
      annualPrice: annualPrice || monthlyPrice || '',
      features: featureLines.join('\n'),
      ...hides,
    };
  });
  return { heading: heading || 'Choose Your Plan', plans };
}

export function serializePricingTogglePlans(heading: string, plans: Array<PricingTogglePlan & Record<string, string>>): { content: string; plans: string } {
  const serialized = joinBlocks(
    plans.map((plan) => {
      const priceLine = `${plan.monthlyPrice} / ${plan.annualPrice || plan.monthlyPrice}`;
      const lines = [plan.name, priceLine];
      if (plan.features.trim()) lines.push(plan.features);
      return appendFieldHideMeta(lines.join('\n'), plan, ['name', 'monthlyPrice', 'annualPrice', 'features']);
    }),
  );
  return { content: heading.trim() || 'Choose Your Plan', plans: serialized };
}

export type TeamMemberSimple = { name: string; role: string } & Hideable<'name' | 'role'>;
export type ColumnBlock = { text: string } & Hideable<'text'>;

export function parseFeatureServiceItems(itemsRaw: string, headingRaw?: string): { heading: string; items: CardItem[] } {
  const raw = itemsRaw;
  if (!raw.trim()) return { heading: headingRaw?.trim() || '', items: [] };
  // Match stats/team: single-item hide meta has no `---` (serializeCardItems → joinBlocks).
  const hasHideMeta = raw.split('\n').some((line) => parseFieldHideMetaLine(line.trim()));
  if (raw.includes('---') || hasHideMeta) {
    const { heading, cards } = parseCardItems(raw, headingRaw, headingRaw);
    return { heading, items: cards };
  }
  const lines = raw.split('\n').map((line) => line.trim()).filter(Boolean);
  return {
    heading: headingRaw?.trim() || '',
    items: lines.map((line) => {
      const pipe = line.indexOf(' | ');
      if (pipe >= 0) {
        return { title: line.slice(0, pipe).trim(), description: line.slice(pipe + 3).trim() };
      }
      return { title: line, description: '' };
    }),
  };
}

export function serializeFeatureServiceItems(heading: string, items: Array<CardItem & Record<string, string>>): { content: string; items: string } {
  const hasDescriptions = items.some((item) => item.description.trim());
  const hasHide = items.some(
    (item) => isEditorFieldHidden(item.titleHide) || isEditorFieldHidden(item.descriptionHide),
  );
  if (!hasDescriptions && !hasHide) {
    return {
      content: heading,
      items: items.map((item) => item.title).join('\n'),
    };
  }
  return serializeCardItems(heading, items);
}

export function parseTeamMembers(membersRaw: string, headingRaw?: string): { heading: string; members: TeamMemberSimple[] } {
  const raw = membersRaw.trim();
  if (!raw) return { heading: headingRaw?.trim() || '', members: [] };
  const hasHideMeta = raw.split('\n').some((line) => parseFieldHideMetaLine(line.trim()));
  const useBlocks = raw.includes('---') || hasHideMeta;
  const blocks = useBlocks
    ? (raw.includes('---') ? splitBlocks(raw) : [raw])
    : raw.split('\n').map((s) => s.trim()).filter(Boolean);
  const members = blocks.map((line) => {
    const { body, hides } = useBlocks ? parseFieldHideFromBlock(line) : { body: line, hides: {} as Record<string, string> };
    if (body.includes('\n')) {
      const [name, ...rest] = body.split('\n');
      return { name: (name || '').trim(), role: rest.join('\n').trim(), ...hides };
    }
    const emDash = body.indexOf(' — ');
    if (emDash >= 0) return { name: body.slice(0, emDash).trim(), role: body.slice(emDash + 3).trim(), ...hides };
    const hyphen = body.indexOf(' - ');
    if (hyphen >= 0) return { name: body.slice(0, hyphen).trim(), role: body.slice(hyphen + 3).trim(), ...hides };
    return { name: body.trim(), role: '', ...hides };
  });
  return { heading: headingRaw?.trim() || '', members };
}

export function serializeTeamMembers(heading: string, members: Array<TeamMemberSimple & Record<string, string>>): { content: string; members: string } {
  const blocks = members.map((member) => {
    const body = member.role ? `${member.name}\n${member.role}` : member.name;
    return appendFieldHideMeta(body, member, ['name', 'role']);
  });
  const hasHide = members.some(
    (member) => isEditorFieldHidden(member.nameHide) || isEditorFieldHidden(member.roleHide),
  );
  const serialized = !hasHide
    ? joinBlocks(blocks)
    : blocks.length === 1
      ? blocks[0]
      : joinBlocks(blocks);
  return { content: heading, members: serialized };
}

export function parseColumnBlocks(contentRaw: string): { columns: ColumnBlock[] } {
  const blocks = splitBlocks(contentRaw);
  return {
    columns: blocks.map((block) => {
      const { body, hides } = parseFieldHideFromBlock(block);
      return { text: body, ...hides };
    }),
  };
}

export function serializeColumnBlocks(columns: Array<ColumnBlock & Record<string, string>>): { content: string } {
  const serialized = joinBlocks(columns.map((column) => appendFieldHideMeta(column.text, column, ['text'])));
  return { content: serialized };
}

export function parseHeroProps(props: Record<string, string>): { title: string; subtitle: string; cta: string; ctaLink: string } {
  const lines = splitContentLines(props.content || '');
  return {
    title: lines[0] ?? '',
    subtitle: lines.slice(1).join('\n'),
    cta: (props.cta || '').trim(),
    ctaLink: (props.ctaLink || '').trim(),
  };
}

export function serializeHeroProps(title: string, subtitle: string, cta: string, ctaLink: string): Record<string, string> {
  return { content: joinTitleBody(title, subtitle), cta: cta.trim(), ctaLink: ctaLink.trim() };
}

export function parseCtaProps(props: Record<string, string>): {
  title: string;
  subtitle: string;
  cta: string;
  ctaLink: string;
  secondaryCta: string;
  secondaryCtaLink: string;
} {
  return {
    title: (props.content || '').trim(),
    subtitle: (props.subtitle || '').trim(),
    cta: (props.cta || '').trim(),
    ctaLink: (props.ctaLink || '').trim(),
    secondaryCta: (props.secondaryCta || '').trim(),
    secondaryCtaLink: (props.secondaryCtaLink || '').trim(),
  };
}

export function serializeCtaProps(
  title: string,
  subtitle: string,
  cta = '',
  ctaLink = '',
  secondaryCta = '',
  secondaryCtaLink = '',
): Record<string, string> {
  return {
    content: title.trim(),
    subtitle: subtitle.trim(),
    cta: cta.trim(),
    ctaLink: ctaLink.trim(),
    secondaryCta: secondaryCta.trim(),
    secondaryCtaLink: secondaryCtaLink.trim(),
  };
}

export function parseHeroSplitProps(props: Record<string, string>): { title: string; subtitle: string; cta: string; ctaLink: string; image: string } {
  const lines = splitContentLines(props.content || '');
  const hasLegacyCtaLine = lines.length >= 3 && !props.cta?.trim();
  return {
    title: lines[0] ?? '',
    subtitle: hasLegacyCtaLine ? (lines[1] ?? '') : lines.slice(1).join('\n'),
    cta: (props.cta || (hasLegacyCtaLine ? lines[2] : '') || '').trim(),
    ctaLink: (props.ctaLink || '').trim(),
    image: (props.image || '').trim(),
  };
}

export function serializeHeroSplitProps(title: string, subtitle: string, cta: string, ctaLink: string, image: string): Record<string, string> {
  return { content: joinTitleBody(title, subtitle), cta: cta.trim(), ctaLink: ctaLink.trim(), image: image.trim() };
}

export function parseCtaSplitProps(props: Record<string, string>): { title: string; subtitle: string; cta: string; link: string } {
  const lines = splitContentLines(props.content || '');
  const hasLegacyCtaLine = lines.length >= 3 && !props.cta?.trim();
  // Display-time scrub: leftover "Stripe-ready" CTAs (Mega polish) must not advertise cloud checkout.
  return {
    title: scrubCloudCheckoutMarketingCopy(lines[0] ?? ''),
    subtitle: scrubCloudCheckoutMarketingCopy(
      props.subtitle || (hasLegacyCtaLine ? lines[1] : lines.slice(1).join('\n')) || '',
    ),
    cta: (props.cta || (hasLegacyCtaLine ? lines[2] : '') || '').trim(),
    link: (props.link || '').trim(),
  };
}

export function serializeCtaSplitProps(title: string, subtitle: string, cta: string, link: string): Record<string, string> {
  return { content: joinTitleBody(title, subtitle), subtitle, cta: cta.trim(), link: link.trim() };
}

export function parseFeatureHighlightProps(props: Record<string, string>): { title: string; description: string; cta: string; link: string; items: string[] } {
  const lines = splitContentLines(props.content || '');
  const hasLegacyCtaLine = lines.length >= 3 && !props.cta?.trim();
  return {
    title: lines[0] ?? '',
    description: (props.subtitle || (hasLegacyCtaLine ? lines[1] : lines.slice(1).join('\n')) || ''),
    cta: (props.cta || (hasLegacyCtaLine ? lines[2] : '') || '').trim(),
    link: (props.link || '').trim(),
    items: (props.items || '').split('\n'),
  };
}

export function serializeFeatureHighlightProps(title: string, description: string, cta: string, link: string, items: string[]): Record<string, string> {
  return {
    content: joinTitleBody(title, description),
    // Keep subtitle in sync — parseFeatureHighlightProps prefers props.subtitle over content body.
    subtitle: description,
    cta: cta.trim(),
    link: link.trim(),
    items: items.join('\n'),
  };
}

export function parseContactProps(props: Record<string, string>): {
  heading: string;
  intro: string;
  email: string;
  phone: string;
  address: string;
} {
  return {
    heading: (props.content || 'Contact').trim(),
    intro: (props.intro || '').trim(),
    email: (props.email || '').trim(),
    phone: (props.phone || '').trim(),
    address: (props.address || '').trim(),
  };
}

export function serializeContactProps(
  heading: string,
  intro: string,
  email: string,
  phone: string,
  address = '',
): Record<string, string> {
  return {
    content: heading.trim() || 'Contact',
    intro: intro.trim(),
    email: email.trim(),
    phone: phone.trim(),
    address: address.trim(),
  };
}

export function parseAboutProps(props: Record<string, string>): { heading: string; body: string } {
  const body = (props.body || '').trim();
  const content = (props.content || '').trim();
  if (body) {
    return { heading: content || 'About', body };
  }
  return { heading: 'About', body: content };
}

export function serializeAboutProps(heading: string, body: string): Record<string, string> {
  return { content: heading.trim() || 'About', body: body.trim() };
}

/** Title + short description + longer body (no CTA). */
export function parseContentIntroProps(props: Record<string, string>): {
  title: string;
  subtitle: string;
  body: string;
} {
  const title = (props.content || '').trim();
  const subtitle = (props.subtitle || '').trim();
  const body = (props.body || '').trim();
  return {
    title: title || 'Section title',
    subtitle,
    body,
  };
}

export function serializeContentIntroProps(
  title: string,
  subtitle: string,
  body: string,
): Record<string, string> {
  return {
    content: title.trim() || 'Section title',
    subtitle: subtitle.trim(),
    body: body.trim(),
  };
}

export function parseNewsletterPopupProps(props: Record<string, string>): { title: string; subtitle: string; buttonText: string } {
  const lines = splitContentLines(props.content || '');
  return {
    title: lines[0] ?? '',
    subtitle: (props.subtitle || lines[1] || ''),
    buttonText: (props.buttonText || '').trim(),
  };
}

export function serializeNewsletterPopupProps(title: string, subtitle: string, buttonText: string): Record<string, string> {
  return { content: title, subtitle, buttonText: buttonText.trim() };
}

export function parseBookingFormProps(props: Record<string, string>): { heading: string; subtitle: string; cta: string; fieldsRaw: string } {
  const lines = splitContentLines(props.content || '');
  return {
    heading: lines[0] ?? '',
    subtitle: lines.slice(1).join('\n'),
    cta: (props.cta || 'Book Now').trim(),
    fieldsRaw: (props.fields || '').trim(),
  };
}

export function serializeBookingFormProps(heading: string, subtitle: string, cta: string, fieldsRaw?: string): Record<string, string> {
  const content = joinTitleBody(heading, subtitle);
  const out: Record<string, string> = { content, cta: cta.trim() || 'Book Now' };
  if (fieldsRaw?.trim()) out.fields = fieldsRaw.trim();
  return out;
}

export const parseCheckoutFormProps = parseBookingFormProps;
export const serializeCheckoutFormProps = serializeBookingFormProps;

export function parseImageTextProps(props: Record<string, string>): { title: string; body: string; cta: string; ctaLink: string; image: string } {
  const lines = splitContentLines(props.content || '');
  const hasLegacyCtaLine = lines.length >= 3 && !props.cta?.trim();
  const bodyFromLines = hasLegacyCtaLine ? (lines[1] ?? '') : lines.slice(1).join('\n');
  const legacyCta = hasLegacyCtaLine ? (lines[2] ?? '') : '';
  const bodyOnly = !props.cta?.trim() && lines.length === 2 ? (lines[1] ?? '') : bodyFromLines;
  const title = lines[0] ?? '';
  let body = (props.body || bodyOnly || '');
  body = stripLeadingTitleFromBody(title, body);
  return {
    title,
    body,
    cta: (props.cta || legacyCta || '').trim(),
    ctaLink: (props.ctaLink || '').trim(),
    image: (props.image || props.src || '').trim(),
  };
}

/** Remove repeated section title from the start of an image-text body. */
export function stripLeadingTitleFromBody(title: string, body: string): string {
  const t = String(title || '').trim();
  let b = String(body || '').replace(/\r\n/g, '\n').trim();
  if (!t || !b) return b;
  const tNorm = t.toLowerCase();
  const firstLine = b.split('\n')[0]?.trim() || '';
  if (firstLine.toLowerCase() === tNorm) {
    b = b.slice(firstLine.length).replace(/^\n+/, '').trim();
  } else if (b.toLowerCase().startsWith(tNorm)) {
    b = b.slice(t.length).replace(/^[\s:–—\-]+/, '').trim();
  }
  return b;
}

/** Truncate prose at a sentence boundary (fallback: hard cut). */
export function truncateAtSentence(raw: string, maxLen: number): string {
  const text = String(raw || '').trim();
  if (text.length <= maxLen) return text;
  const slice = text.slice(0, maxLen);
  const breakAt = Math.max(slice.lastIndexOf('. '), slice.lastIndexOf('! '), slice.lastIndexOf('? '), slice.lastIndexOf('\n'));
  if (breakAt >= Math.floor(maxLen * 0.45)) {
    return slice.slice(0, breakAt + 1).trim();
  }
  const space = slice.lastIndexOf(' ');
  return `${(space > 40 ? slice.slice(0, space) : slice).trim()}…`;
}

export function serializeImageTextProps(title: string, body: string, cta: string, ctaLink: string, image: string): Record<string, string> {
  return {
    content: joinTitleBody(title, body),
    body,
    cta: cta.trim(),
    ctaLink: ctaLink.trim(),
    image: image.trim(),
  };
}

export function parseVideoProps(props: Record<string, string>): { caption: string; src: string } {
  return { caption: (props.content || '').trim(), src: (props.src || '').trim() };
}

export function serializeVideoProps(caption: string, src: string): Record<string, string> {
  return { content: caption.trim(), src: src.trim() };
}

export function parseFormBlockProps(props: Record<string, string>): { heading: string; submitLabel: string; fieldsRaw: string } {
  return {
    heading: (props.content || '').trim(),
    submitLabel: (props.submitLabel || props.buttonText || 'Send').trim(),
    fieldsRaw: (props.fields || '').trim(),
  };
}

export function serializeFormBlockProps(heading: string, submitLabel: string, fieldsRaw?: string): Record<string, string> {
  const out: Record<string, string> = { content: heading.trim(), submitLabel: submitLabel.trim() || 'Send' };
  if (fieldsRaw?.trim()) out.fields = fieldsRaw.trim();
  return out;
}

export function parseNewsletterBlockProps(props: Record<string, string>): { heading: string; buttonText: string } {
  return {
    heading: (props.content || '').trim(),
    buttonText: (props.buttonText || 'Subscribe').trim(),
  };
}

export function serializeNewsletterBlockProps(heading: string, buttonText: string): Record<string, string> {
  return { content: heading.trim(), buttonText: buttonText.trim() || 'Subscribe' };
}

export function parseGalleryImageUrls(itemsRaw: string, headingRaw?: string): { heading: string; urls: string[] } {
  const slides = parseGallerySlides(itemsRaw);
  return { heading: headingRaw?.trim() || 'Gallery', urls: slides.map((s) => s.imageUrl) };
}

/** Manual gallery slides: URL lines, optional `url | caption | /link`, or `---` blocks. */
export function parseGallerySlides(
  itemsRaw: string,
): Array<{ imageUrl: string; caption?: string; link?: string }> {
  const raw = (itemsRaw ?? '').trim();
  if (!raw) return [];

  const fromBlock = (block: string) => {
    const lines = block
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    if (!lines.length) return null;
    const first = lines[0];
    if (first.includes('|')) {
      const parts = first.split('|').map((p) => p.trim());
      const imageUrl = parts[0] || '';
      if (!imageUrl) return null;
      return {
        imageUrl,
        ...(parts[1] ? { caption: parts[1] } : {}),
        ...(parts[2] ? { link: parts[2] } : {}),
      };
    }
    return {
      imageUrl: first,
      ...(lines[1] ? { caption: lines[1] } : {}),
      ...(lines[2] ? { link: lines[2] } : {}),
    };
  };

  if (raw.includes('---')) {
    return splitBlocks(raw)
      .map((block) => fromBlock(block))
      .filter((s): s is { imageUrl: string; caption?: string; link?: string } => Boolean(s?.imageUrl));
  }

  return raw
    .split('\n')
    .map((line) => fromBlock(line))
    .filter((s): s is { imageUrl: string; caption?: string; link?: string } => Boolean(s?.imageUrl));
}

export function serializeGalleryImageUrls(heading: string, urls: string[]): { content: string; items: string } {
  return { content: heading.trim() || 'Gallery', items: urls.join('\n') };
}

export function serializeGallerySlides(
  heading: string,
  slides: Array<{ imageUrl: string; caption?: string; link?: string }>,
): { content: string; items: string } {
  const hasMeta = slides.some((s) => Boolean(s.caption?.trim() || s.link?.trim()));
  const items = hasMeta
    ? slides
        .map((s) => {
          const lines = [s.imageUrl.trim()];
          if (s.caption?.trim()) lines.push(s.caption.trim());
          if (s.link?.trim()) lines.push(s.link.trim());
          return lines.join('\n');
        })
        .join('\n---\n')
    : slides.map((s) => s.imageUrl.trim()).filter(Boolean).join('\n');
  return { content: heading.trim() || 'Gallery', items };
}

export function parseStructuredBlockProps(
  type: string,
  props: Record<string, string>
): { heading: string; items: Array<Record<string, string>>; itemFields: string[] } {
  if (type === 'pricing') {
    const { heading, plans } = parsePricingPlans(props.plans || '', props.content);
    return {
      heading,
      items: plans.map((p) => ({ ...p })),
      itemFields: ['name', 'price', 'description', 'nameHide', 'priceHide', 'descriptionHide'],
    };
  }
  if (type === 'cards') {
    const { heading, cards } = parseCardItems(props.items || '', props.content, props.content);
    return {
      heading,
      items: cards.map((c) => ({
        title: c.title,
        description: c.description,
        imageUrl: c.imageUrl || '',
        titleHide: c.titleHide || '',
        descriptionHide: c.descriptionHide || '',
      })),
      itemFields: ['imageUrl', 'title', 'description', 'titleHide', 'descriptionHide'],
    };
  }
  if (type === 'tabs') {
    const { tabs } = parseTabItems(props.content || '');
    return {
      heading: '',
      items: tabs.map((t) => ({ ...t })),
      itemFields: ['label', 'content'],
    };
  }
  if (type === 'steps') {
    const { steps } = parseStepItems(props.content || '');
    return {
      heading: '',
      items: steps.map((s) => ({ ...s })),
      itemFields: ['title', 'description'],
    };
  }
  if (type === 'blog-grid') {
    const { heading, posts } = parseBlogGridItems(props.items || '', props.content);
    return {
      heading,
      items: posts.map((p) => ({ ...p })),
      itemFields: ['title', 'date', 'excerpt', 'link', 'titleHide', 'dateHide', 'excerptHide', 'linkHide'],
    };
  }
  if (type === 'team-grid') {
    const { heading, members } = parseTeamGridMembers(props.members || props.items || '', props.content);
    return {
      heading,
      items: members.map((m) => ({
        name: m.name,
        role: m.role,
        bio: m.bio,
        imageUrl: m.imageUrl || '',
        nameHide: m.nameHide || '',
        roleHide: m.roleHide || '',
        bioHide: m.bioHide || '',
      })),
      itemFields: ['imageUrl', 'name', 'role', 'bio', 'nameHide', 'roleHide', 'bioHide'],
    };
  }
  if (type === 'product-grid') {
    const { heading, products } = parseProductGridItems(props.items || '', props.content);
    return {
      heading,
      items: products.map((p) => ({
        name: p.name,
        price: p.price,
        description: p.description,
        link: p.link,
        imageUrl: p.imageUrl || '',
        nameHide: p.nameHide || '',
        priceHide: p.priceHide || '',
        descriptionHide: p.descriptionHide || '',
        linkHide: p.linkHide || '',
      })),
      itemFields: ['imageUrl', 'name', 'price', 'description', 'link', 'nameHide', 'priceHide', 'descriptionHide', 'linkHide'],
    };
  }
  if (type === 'timeline') {
    const { entries } = parseTimelineEntries(props.content || '');
    return {
      heading: '',
      items: entries.map((entry) => ({ ...entry })),
      itemFields: ['year', 'title', 'description', 'yearHide', 'titleHide', 'descriptionHide'],
    };
  }
  if (type === 'menu-section') {
    const { heading, categories } = parseMenuSectionCategories(props.items || '', props.content);
    return {
      heading,
      items: categories.map((category) => ({ ...category })),
      itemFields: ['categoryName', 'menuItems', 'categoryNameHide', 'menuItemsHide'],
    };
  }
  if (type === 'stats') {
    const { items } = parseStatItems(props.content || '');
    return {
      heading: '',
      items: items.map((item) => ({ ...item })),
      itemFields: ['value', 'label', 'valueHide', 'labelHide'],
    };
  }
  if (type === 'icon-list') {
    const { heading, items } = parseIconListItems(props.items || '', props.content, props.content);
    return {
      heading,
      items: items.map((item) => ({ ...item })),
      itemFields: ['icon', 'text', 'textHide'],
    };
  }
  if (type === 'progress-bars') {
    const { heading, items } = parseProgressBarItems(props.items || '', props.content, props.content);
    return {
      heading,
      items: items.map((item) => ({ ...item })),
      itemFields: ['label', 'percent', 'labelHide', 'percentHide'],
    };
  }
  if (type === 'badges') {
    const { items } = parseBadgeLabels(props.content || '');
    return {
      heading: '',
      items: items.map((item) => ({ ...item })),
      itemFields: ['label', 'labelHide'],
    };
  }
  if (type === 'navbar' || type === 'sticky-header') {
    const { brand, links } = parseNavbarContent(props.content || '');
    return {
      heading: brand,
      items: links.map((link) => {
        const item: Record<string, string> = {
          label: link.label || '',
          url: link.url || '',
        };
        if (link.labelHide) item.labelHide = String(link.labelHide);
        const childText = childrenToMultiline(link.children);
        if (childText) item.children = childText;
        return item;
      }),
      itemFields: ['label', 'url', 'children', 'labelHide'],
    };
  }
  if (type === 'footer-nav' || type === 'social-links') {
    const { links } = parseLinkLines(props.content || '');
    return {
      heading: '',
      items: links.map((link) => ({
        label: link.label || '',
        url: link.url || '',
        ...(link.labelHide ? { labelHide: String(link.labelHide) } : {}),
      })),
      itemFields: ['label', 'url', 'labelHide'],
    };
  }
  if (type === 'rich-footer') {
    const { columns } = parseRichFooterColumns(props.content || '');
    return {
      heading: '',
      items: columns.map((column) => ({ ...column })),
      itemFields: ['columnHeading', 'columnLines', 'columnHeadingHide', 'columnLinesHide'],
    };
  }
  if (type === 'logo-strip') {
    const { urls } = parseLogoUrls(props.content || '');
    return {
      heading: '',
      items: urls.map((item) => ({ url: item.url })),
      itemFields: ['url'],
    };
  }
  if (type === 'carousel' || type === 'slider') {
    const { slides } = parseCarouselSlides(props.content || '');
    return {
      heading: '',
      items: slides.map((slide) => ({
        ...slide,
        imageUrl: slide.imageUrl,
        heading: slide.heading,
        subtitle: slide.subtitle,
        ctaLabel: slide.ctaLabel,
        ctaLink: slide.ctaLink,
        text: slide.text,
        captionPosition: slide.captionPosition || '',
        overlayColor: slide.overlayColor || '',
        overlayOpacity: slide.overlayOpacity || '',
        titleColor: slide.titleColor || '',
        titleGradient: slide.titleGradient || '',
        titleFontSize: slide.titleFontSize || '',
        subtitleColor: slide.subtitleColor || '',
        subtitleFontSize: slide.subtitleFontSize || '',
        buttonColor: slide.buttonColor || '',
        buttonBg: slide.buttonBg || '',
        buttonGradient: slide.buttonGradient || '',
        buttonFontSize: slide.buttonFontSize || '',
        headingHide: slide.headingHide || '',
        subtitleHide: slide.subtitleHide || '',
        ctaLabelHide: slide.ctaLabelHide || '',
        textHide: slide.textHide || '',
        slideHide: slide.slideHide || '',
      })),
      itemFields: [
        'imageUrl',
        'heading',
        'subtitle',
        'ctaLabel',
        'ctaLink',
        'text',
        'captionPosition',
        'overlayColor',
        'overlayOpacity',
        'titleColor',
        'titleGradient',
        'titleFontSize',
        'subtitleColor',
        'subtitleFontSize',
        'buttonColor',
        'buttonBg',
        'buttonGradient',
        'buttonFontSize',
        'headingHide',
        'subtitleHide',
        'ctaLabelHide',
        'textHide',
        'slideHide',
      ],
    };
  }
  if (type === 'testimonials') {
    const { heading, quotes } = parseTestimonialQuotes(props.quotes || '', props.content, props.content);
    return {
      heading,
      items: quotes.map((item) => ({
        ...item,
        imageUrl: item.imageUrl || '',
      })),
      itemFields: ['imageUrl', 'quote', 'quoteHide'],
    };
  }
  if (type === 'testimonials-slider') {
    const { quotes } = parseTestimonialSliderQuotes(props.content || '', props.quotes);
    return {
      heading: '',
      items: quotes.map((item) => ({
        ...item,
        imageUrl: item.imageUrl || '',
      })),
      itemFields: ['imageUrl', 'quote', 'quoteHide'],
    };
  }
  if (type === 'pricing-toggle') {
    const { heading, plans } = parsePricingTogglePlans(props.plans || '', props.content, props.content);
    return {
      heading,
      items: plans.map((plan) => ({ ...plan })),
      itemFields: ['name', 'monthlyPrice', 'annualPrice', 'features', 'nameHide', 'monthlyPriceHide', 'annualPriceHide', 'featuresHide'],
    };
  }
  if (type === 'features' || type === 'services') {
    const { heading, items } = parseFeatureServiceItems(props.items || '', props.content);
    return {
      heading,
      items: items.map((item) => ({ ...item, title: item.title, description: item.description })),
      itemFields: ['title', 'description', 'titleHide', 'descriptionHide'],
    };
  }
  if (type === 'team') {
    const { heading, members } = parseTeamMembers(props.members || '', props.content);
    return {
      heading,
      items: members.map((member) => ({ ...member })),
      itemFields: ['name', 'role', 'nameHide', 'roleHide'],
    };
  }
  if (type === 'gallery') {
    const { heading, urls } = parseGalleryImageUrls(props.items || '', props.content);
    const slides = parseGallerySlides(props.items || '');
    const resolved = slides.length
      ? slides
      : urls.map((url) => ({ imageUrl: url, caption: undefined as string | undefined, link: undefined as string | undefined }));
    return {
      heading,
      items: resolved.map((s) => ({
        imageUrl: s.imageUrl,
        caption: s.caption || '',
        link: s.link || '',
      })),
      itemFields: ['imageUrl', 'caption', 'link'],
    };
  }
  if (type === 'gallery-carousel') {
    const heading = String(props.content || '').trim() || 'Image Gallery';
    const slideSource = String(props.items || '');
    const { slides } = parseCarouselSlides(slideSource);
    return {
      heading,
      items: slides.map((slide) => ({
        imageUrl: slide.imageUrl,
        caption: slide.heading,
        link: slide.ctaLink,
        captionHide: slide.headingHide || '',
      })),
      itemFields: ['imageUrl', 'caption', 'link'],
    };
  }
  if (type === 'columns') {
    const { columns } = parseColumnBlocks(props.content || '');
    return {
      heading: '',
      items: columns.map((column) => ({ ...column })),
      itemFields: ['text', 'textHide'],
    };
  }
  const source = props.items || props.content || '';
  const { heading, items } = parseQaItems(source, props.content);
  return {
    heading: type === 'accordion' && props.items ? (props.content || heading) : heading,
    items: items.map((i) => ({ ...i, question: i.question, answer: i.answer })),
    itemFields: ['question', 'answer', 'questionHide', 'answerHide'],
  };
}

export function serializeStructuredBlockProps(
  type: string,
  heading: string,
  items: Array<Record<string, string>>
): Record<string, string> {
  if (type === 'pricing') {
    const plans = items.map((item) => ({
      ...item,
      name: item.name || '',
      price: item.price || '',
      description: item.description || '',
    }));
    return serializePricingPlans(heading, plans);
  }
  if (type === 'cards') {
    const cards = items.map((item) => ({
      ...item,
      title: item.title || '',
      description: item.description || '',
      imageUrl: item.imageUrl || '',
    }));
    return serializeCardItems(heading, cards);
  }
  if (type === 'tabs') {
    const tabs = items.map((item) => ({
      ...item,
      label: item.label || '',
      content: item.content || '',
    }));
    return serializeTabItems(tabs);
  }
  if (type === 'steps') {
    const steps = items.map((item) => ({
      ...item,
      title: item.title || '',
      description: item.description || '',
    }));
    return serializeStepItems(steps);
  }
  if (type === 'blog-grid') {
    const posts = items.map((item) => ({
      ...item,
      title: item.title || '',
      date: item.date || '',
      excerpt: item.excerpt || '',
      link: item.link || '',
    }));
    return { ...serializeBlogGridItems(heading, posts), source: 'manual' };
  }
  if (type === 'team-grid') {
    const members = items.map((item) => ({
      ...item,
      name: item.name || '',
      role: item.role || '',
      bio: item.bio || '',
      imageUrl: item.imageUrl || '',
    }));
    return serializeTeamGridMembers(heading, members);
  }
  if (type === 'product-grid') {
    const products = items.map((item) => ({
      ...item,
      name: item.name || '',
      price: item.price || '',
      description: item.description || '',
      link: item.link || '',
      imageUrl: item.imageUrl || '',
    }));
    return serializeProductGridItems(heading, products);
  }
  if (type === 'timeline') {
    const entries = items.map((item) => ({
      ...item,
      year: item.year || '',
      title: item.title || '',
      description: item.description || '',
    }));
    return serializeTimelineEntries(entries);
  }
  if (type === 'menu-section') {
    const categories = items.map((item) => ({
      ...item,
      categoryName: item.categoryName || '',
      menuItems: item.menuItems || '',
    }));
    return serializeMenuSectionCategories(heading, categories);
  }
  if (type === 'stats') {
    const stats = items.map((item) => ({
      ...item,
      value: item.value || '',
      label: item.label || '',
    }));
    return serializeStatItems(stats);
  }
  if (type === 'icon-list') {
    const icons = items.map((item) => ({
      ...item,
      icon: item.icon || '✓',
      text: item.text || '',
    }));
    return serializeIconListItems(heading, icons);
  }
  if (type === 'progress-bars') {
    const bars = items.map((item) => ({
      ...item,
      label: item.label || '',
      percent: item.percent || '',
    }));
    return serializeProgressBarItems(heading, bars);
  }
  if (type === 'badges') {
    const labels = items.map((item) => ({ ...item, label: item.label || '' }));
    return serializeBadgeLabels(labels);
  }
  if (type === 'navbar' || type === 'sticky-header') {
    const links = items.map((item) => {
      const children = parseChildrenField(item.children);
      const link: NavLink & Record<string, string> = {
        ...item,
        label: item.label || '',
        url: item.url || '',
      };
      if (children?.length) link.children = children;
      else delete link.children;
      return link;
    });
    return serializeNavbarContent(heading, links);
  }
  if (type === 'footer-nav' || type === 'social-links') {
    const links = items.map((item) => ({
      ...item,
      label: item.label || '',
      url: item.url || '',
    }));
    return serializeLinkLines(links);
  }
  if (type === 'rich-footer') {
    const columns = items.map((item) => ({
      ...item,
      columnHeading: item.columnHeading || '',
      columnLines: item.columnLines || '',
    }));
    return serializeRichFooterColumns(columns);
  }
  if (type === 'logo-strip') {
    const urls = items.map((item) => ({ url: item.url || '' }));
    return serializeLogoUrls(urls);
  }
  if (type === 'carousel' || type === 'slider') {
    const slides = items.map((item) => ({
      ...item,
      imageUrl: item.imageUrl || '',
      heading: item.heading || '',
      subtitle: item.subtitle || '',
      ctaLabel: item.ctaLabel || '',
      ctaLink: item.ctaLink || '',
      text: item.text || '',
      captionPosition: item.captionPosition || '',
      overlayColor: item.overlayColor || '',
      overlayOpacity: item.overlayOpacity || '',
      titleColor: item.titleColor || '',
      titleGradient: item.titleGradient || '',
      titleFontSize: item.titleFontSize || '',
      subtitleColor: item.subtitleColor || '',
      subtitleFontSize: item.subtitleFontSize || '',
      buttonColor: item.buttonColor || '',
      buttonBg: item.buttonBg || '',
      buttonGradient: item.buttonGradient || '',
      buttonFontSize: item.buttonFontSize || '',
      headingHide: item.headingHide || '',
      subtitleHide: item.subtitleHide || '',
      ctaLabelHide: item.ctaLabelHide || '',
      textHide: item.textHide || '',
      slideHide: item.slideHide || '',
    }));
    return serializeCarouselSlides(slides as CarouselSlide[]);
  }
  if (type === 'testimonials') {
    const quotes = items.map((item) => ({
      ...item,
      quote: item.quote || '',
      imageUrl: item.imageUrl || '',
    }));
    return serializeTestimonialQuotes(heading, quotes);
  }
  if (type === 'testimonials-slider') {
    const quotes = items.map((item) => ({
      ...item,
      quote: item.quote || '',
      imageUrl: item.imageUrl || '',
    }));
    return serializeTestimonialSliderQuotes(quotes);
  }
  if (type === 'pricing-toggle') {
    const plans = items.map((item) => ({
      ...item,
      name: item.name || '',
      monthlyPrice: item.monthlyPrice || '',
      annualPrice: item.annualPrice || '',
      features: item.features || '',
    }));
    return serializePricingTogglePlans(heading, plans);
  }
  if (type === 'features' || type === 'services') {
    const featureItems = items.map((item) => ({
      ...item,
      title: item.title || '',
      description: item.description || '',
    }));
    return serializeFeatureServiceItems(heading, featureItems);
  }
  if (type === 'team') {
    const members = items.map((item) => ({
      ...item,
      name: item.name || '',
      role: item.role || '',
    }));
    return serializeTeamMembers(heading, members);
  }
  if (type === 'gallery') {
    const slides = items.map((item) => ({
      imageUrl: item.imageUrl || '',
      caption: item.caption || '',
      link: item.link || '',
    }));
    return serializeGallerySlides(heading, slides);
  }
  if (type === 'gallery-carousel') {
    const slides = items.map((item) => ({
      imageUrl: item.imageUrl || '',
      heading: item.caption || '',
      headingHide: item.captionHide || '',
      subtitle: '',
      ctaLabel: '',
      ctaLink: item.link || '',
      captionPosition: 'bottom-center',
      overlayColor: '',
      overlayOpacity: '',
      text: '',
    }));
    return {
      content: heading,
      items: serializeCarouselSlides(slides).content,
    };
  }
  if (type === 'columns') {
    const columns = items.map((item) => ({ ...item, text: item.text || '' }));
    return serializeColumnBlocks(columns);
  }
  const qa = items.map((item) => ({
    ...item,
    question: item.question || '',
    answer: item.answer || '',
  }));
  const serialized = serializeQaItems(heading, qa);
  if (type === 'faq') {
    return { content: serialized.content, items: serialized.items };
  }
  return { content: serialized.content, items: serialized.items };
}
